/**
 * Rich "everything you need to know" content per service, rendered by <ServiceContent>.
 * Every field is optional except `intro` and `faqs` — leave a field out rather than padding it.
 * `process` defaults to a generic 5-step SolveTax flow when not overridden.
 */
export const SERVICE_CONTENT = {
  // ===========================================================================
  // GST
  // ===========================================================================
  GST_REGISTRATION: {
    intro: [
      'GST (Goods and Services Tax) registration gives your business a unique GSTIN and the legal ability to collect GST, issue tax-compliant invoices and claim input tax credit on your purchases. It is mandatory once your turnover crosses the threshold for your state and business type, and for certain categories of business regardless of turnover.',
      "With SolveTax, you don't fill out the government portal yourself — a dedicated CA collects your details, prepares the application, files it, and stays with your case until your GSTIN is issued.",
    ],
    table: {
      title: 'Turnover limits for GST registration',
      columns: ['Business type', 'Annual turnover limit', 'Note'],
      rows: [
        ['Goods — normal category states', '₹40 lakhs', 'Registration becomes mandatory once annual turnover crosses this limit'],
        ['Goods — special category states', '₹20 lakhs', 'Lower threshold applies in North-Eastern & hill states'],
        ['Services — normal category states', '₹20 lakhs', 'Applies to service providers across most of India'],
        ['Services — special category states', '₹10 lakhs', 'Lower threshold applies in North-Eastern & hill states'],
        ['E-commerce sellers', 'No minimum', 'Mandatory regardless of turnover if selling through an online marketplace'],
      ],
    },
    documents: [
      'PAN of the business/proprietor',
      'Aadhaar and a photograph of the proprietor/partners/directors',
      'Proof of business address (electricity bill + rent agreement/NOC)',
      'Bank account proof (cancelled cheque or bank statement)',
      'Partnership deed / incorporation certificate, for firms and companies',
    ],
    eligibilityTitle: 'Who needs GST registration?',
    eligibility: [
      'Businesses whose turnover crosses the applicable threshold in a financial year',
      'E-commerce sellers and operators, irrespective of turnover',
      'Businesses supplying goods or services across state lines',
      'Exporters and importers',
      'Casual and non-resident taxable persons',
      'Anyone wanting to voluntarily register to claim input tax credit',
    ],
    benefits: [
      'Legally collect GST from customers and issue GST-compliant invoices',
      'Claim input tax credit on GST paid for business purchases and expenses',
      'Sell on e-commerce marketplaces like Amazon, Flipkart and Meesho',
      'Take on inter-state and B2B clients who require a valid GSTIN',
      'Build credibility — a GSTIN signals a registered, compliant business',
      'Apply for business loans and tenders that require GST registration',
    ],
    process: [
      { title: 'Share your business details', desc: 'Fill the form on this page with your name, mobile number and business type — takes under a minute.' },
      { title: 'A dedicated CA calls you', desc: 'Our GST expert calls within 2 hours to understand your business and confirm the documents needed.' },
      { title: 'We prepare & file your application', desc: 'Your CA fills the GST REG-01 form, uploads documents and files on the official GST portal on your behalf.' },
      { title: 'ARN & status tracking', desc: 'You get your Application Reference Number immediately, and we track it through verification for you.' },
      { title: 'GSTIN & certificate delivered', desc: 'Once approved, we send your 15-digit GSTIN and registration certificate — ready to start invoicing.' },
    ],
    timeline: '3–7 working days from document submission to GSTIN issuance, subject to verification by the GST department. Pricing is quoted upfront with no hidden charges.',
    mistakes: [
      { title: 'Mismatched PAN and Aadhaar details', desc: 'Even a small spelling or date-of-birth mismatch between PAN and Aadhaar is one of the most common reasons applications get flagged.' },
      { title: 'Incomplete address proof', desc: 'Submitting a rent agreement without the matching NOC or an outdated electricity bill often triggers a query from the department.' },
      { title: 'Wrong business constitution selected', desc: 'Choosing "Proprietorship" instead of "Partnership" (or similar) leads to rejection once documents are cross-checked.' },
      { title: 'Delaying registration after crossing the threshold', desc: 'Waiting too long after crossing the turnover limit can mean paying GST liability retroactively along with interest.' },
    ],
    penalties: [
      { title: 'No registration despite being liable', desc: '10% of the tax due (minimum ₹10,000), or 100% of the tax due if the default is deliberate.' },
      { title: 'Late GST return filing', desc: 'Late fee per day of delay (capped per return), plus interest at 18% per annum on the outstanding tax amount.' },
      { title: 'Incorrect invoicing without GSTIN', desc: 'Can invite penalties and disallowance of input tax credit for the recipient of the invoice.' },
    ],
    faqs: [
      { q: 'What is a GSTIN?', a: 'GSTIN (GST Identification Number) is the unique 15-digit number assigned to every business registered under GST. It is based on your PAN and state code, and must appear on every invoice you issue.' },
      { q: 'Is GST registration mandatory for my business?', a: 'It is mandatory once your annual turnover crosses the applicable threshold (see the table above), and always mandatory for e-commerce sellers, inter-state suppliers and a few other categories — regardless of turnover.' },
      { q: 'Can I register for GST voluntarily even if I am below the limit?', a: 'Yes. Many small businesses register voluntarily to claim input tax credit on purchases and to be able to bill larger, GST-registered clients.' },
      { q: 'How long does GST registration take?', a: 'Most applications are approved within 3–7 working days once documents are verified. Some get approved even faster under the current rules; a physical verification, if required, can add a few extra days.' },
      { q: 'What documents do I need?', a: 'PAN, Aadhaar, a photograph, proof of your business address and a cancelled cheque or bank statement, plus entity-specific documents (partnership deed, incorporation certificate, etc.) — see the checklist above for your business type.' },
      { q: 'What happens if my application gets rejected?', a: 'Rejections usually come down to mismatched PAN/Aadhaar details or an address that could not be verified. Your dedicated CA reviews everything before filing to avoid this, and handles any resubmission if it happens.' },
      { q: 'How much does GST registration cost?', a: 'The government charges no fee for GST registration itself. SolveTax quotes a single, upfront professional fee for the full done-for-you service — no hidden charges.' },
      { q: 'Can I register for GST without a commercial office?', a: 'Yes. You can register using your home address as the principal place of business, subject to providing valid address proof for that location.' },
      { q: 'Do freelancers need GST registration?', a: 'Only if your annual receipts cross the applicable services threshold, or if you take on inter-state clients or sell through platforms that require a GSTIN.' },
      { q: 'What is the difference between GST registration and GST filing?', a: 'Registration is a one-time process to obtain your GSTIN. Filing is the ongoing monthly, quarterly or annual submission of your GST returns once registered.' },
      { q: 'Can I cancel my GST registration later?', a: 'Yes, GST registration can be surrendered if your business closes or turnover falls below the threshold — SolveTax also handles GST cancellation as a separate service.' },
      { q: 'Will I need a Digital Signature Certificate (DSC)?', a: 'Individuals and proprietors can typically use Aadhaar e-signature; companies and LLPs are required to sign the application using a Class 3 DSC.' },
    ],
  },

  GST_FILING: {
    intro: [
      'Once you hold a GSTIN, you must file GST returns regularly — reporting your sales, purchases and tax liability to the department. Missing a due date attracts a daily late fee and interest, even if you have no transactions to report that month.',
      'SolveTax matches your books to your GSTR-1 (outward supplies) and GSTR-3B (summary return + tax payment) every cycle, so you never have to track due dates or reconcile numbers yourself.',
    ],
    eligibilityTitle: 'Who needs to file GST returns?',
    eligibility: [
      'Every business holding an active GSTIN, even with zero transactions in a period',
      'Regular scheme taxpayers filing monthly GSTR-1 and GSTR-3B',
      'Businesses under the QRMP scheme filing quarterly (see GST Quarterly Filing)',
      'E-commerce sellers whose GSTIN is used for supplies via a marketplace',
    ],
    documents: [
      'Sales invoices and credit/debit notes for the period',
      'Purchase invoices and expense records for input tax credit',
      'Bank statement for the period (for reconciliation)',
      'Previous period\'s filed return and GSTR-2B statement',
      'E-way bills, if applicable to your goods movement',
    ],
    benefits: [
      'Avoid late fees and 18% p.a. interest on unpaid tax',
      'Keep your GSTIN active — non-filing for 6+ months can lead to cancellation',
      'Stay eligible to claim input tax credit, which lapses if returns are delayed',
      'Maintain a clean compliance record for loans, tenders and vendor onboarding',
    ],
    process: [
      { title: 'Share your sales & purchase data', desc: 'Send your invoices or Tally/Zoho export each month — we tell you exactly what format works best.' },
      { title: 'We reconcile & prepare your return', desc: 'Your accountant matches GSTR-2B credit, reconciles books and prepares GSTR-1 and GSTR-3B.' },
      { title: 'You review & approve', desc: 'We share a summary of tax payable before filing — no surprises.' },
      { title: 'We file before the due date', desc: 'Returns are filed on the GST portal well ahead of the deadline, every cycle.' },
      { title: 'Acknowledgement shared', desc: 'You get the filed return and acknowledgement for your records.' },
    ],
    timeline: 'GSTR-1 is due on the 11th and GSTR-3B on the 20th of the following month for regular taxpayers. We build in a buffer so filings go in well before the deadline.',
    mistakes: [
      { title: 'Claiming ITC not reflected in GSTR-2B', desc: 'Claiming input tax credit that your supplier hasn\'t reported in their own return is a common trigger for a mismatch notice.' },
      { title: 'Mismatched invoice values between GSTR-1 and books', desc: 'Small rounding or data-entry differences between your books and filed return accumulate into reconciliation headaches at year-end.' },
      { title: 'Filing "Nil" returns late out of neglect', desc: 'Businesses often assume a zero-transaction month needs no filing — the late fee still applies from day one.' },
    ],
    penalties: [
      { title: 'Late filing fee', desc: '₹50/day (₹20/day for nil returns) per return, subject to a cap, charged per Act (CGST + SGST).' },
      { title: 'Interest on late tax payment', desc: '18% per annum on the outstanding tax liability, calculated from the due date until payment.' },
      { title: 'GSTIN suspension/cancellation', desc: 'Continuous non-filing for 6 months or more can lead to the department cancelling your registration.' },
    ],
    faqs: [
      { q: 'What is GSTR-1?', a: 'GSTR-1 is the monthly (or quarterly) return where you report details of all outward supplies (sales) made during the period.' },
      { q: 'What is GSTR-3B?', a: 'GSTR-3B is a summary return declaring total sales, purchases, input tax credit and net tax payable — filed and paid alongside GSTR-1.' },
      { q: 'Do I need to file if I had no sales this month?', a: 'Yes. A "Nil" GSTR-1 and GSTR-3B must still be filed to avoid late fees and keep your GSTIN active.' },
      { q: 'What happens if I miss a due date?', a: 'A late fee starts accruing per day of delay, along with 18% annual interest on any unpaid tax — and you cannot file the next period\'s return until the pending one is filed.' },
      { q: 'Can SolveTax handle both monthly and quarterly filing?', a: 'Yes — we support both the regular monthly scheme and the QRMP quarterly scheme, whichever applies to your registration.' },
      { q: 'Do you also reconcile input tax credit?', a: 'Yes, we match your purchases against GSTR-2B every cycle so you claim the input tax credit you are actually eligible for.' },
      { q: 'What format should I send my sales and purchase data in?', a: 'Whatever you already use — a Tally/Zoho/Excel export, scanned invoices, or a live login to your accounting software all work; we\'ll tell you the easiest option for your setup.' },
      { q: 'Can you also handle e-way bills alongside GST filing?', a: 'Yes, if your business moves goods above the e-way bill threshold, we can generate and track these alongside your regular return filing.' },
    ],
  },

  GST_Q_FILING: {
    intro: [
      "The QRMP (Quarterly Return Monthly Payment) scheme lets small taxpayers with turnover up to ₹5 crore file GSTR-1 and GSTR-3B quarterly instead of monthly, while still paying tax monthly through a simple challan. It's designed to reduce the compliance burden for smaller businesses.",
      'SolveTax manages both sides for you — the monthly tax payment (via PMT-06 challan) and the quarterly return filing — so you get the reduced paperwork without missing the monthly payment obligation that still applies.',
    ],
    eligibilityTitle: 'Who can opt for QRMP?',
    eligibility: [
      'Registered taxpayers with aggregate annual turnover up to ₹5 crore in the previous financial year',
      'Businesses that have filed their last due GSTR-3B before opting in',
      'Available separately for each GSTIN, so multi-state businesses can choose per registration',
    ],
    documents: [
      'Previous quarter\'s filed GSTR-1 and GSTR-3B',
      'Sales and purchase records for the quarter',
      'Bank statement for the relevant period',
      'GSTR-2B statements for each month of the quarter',
    ],
    benefits: [
      'File GSTR-1 and GSTR-3B just 4 times a year instead of 12',
      'Lower compliance workload for small businesses and shopkeepers',
      'Still get full input tax credit — quarterly filing does not restrict ITC',
      'Simple fixed-sum or self-assessment monthly tax payment options',
    ],
    process: [
      { title: 'Opt-in confirmed', desc: 'We check your eligibility and confirm your QRMP opt-in status for the GSTIN.' },
      { title: 'Monthly tax estimate paid', desc: 'A simple monthly challan (PMT-06) is prepared and paid for the first two months of the quarter.' },
      { title: 'Quarterly return prepared', desc: 'Sales and purchases for all three months are reconciled into the quarterly GSTR-1 and GSTR-3B.' },
      { title: 'Filed & true-up', desc: 'The quarterly return is filed with the exact liability, adjusting for the monthly payments already made.' },
    ],
    timeline: 'Monthly tax payment is due by the 25th of the following month; the quarterly GSTR-1/3B is due by the 13th/22nd-24th of the month after quarter-end depending on your state.',
    mistakes: [
      { title: 'Forgetting the monthly challan payment', desc: 'Businesses sometimes assume "quarterly filing" means no monthly obligation at all — the monthly tax payment is still mandatory even though the return itself is quarterly.' },
      { title: 'Using the wrong payment method (fixed sum vs self-assessment)', desc: 'Choosing an option that doesn\'t match your actual month-to-month revenue pattern can lead to a larger-than-expected true-up and interest at quarter-end.' },
    ],
    faqs: [
      { q: 'Do I still pay tax every month under QRMP?', a: 'Yes — you pay an estimated tax amount monthly via a simple challan (PMT-06), and true up the exact liability when you file the quarterly return.' },
      { q: 'Can I switch between monthly and QRMP filing?', a: 'Yes, eligible taxpayers can opt in or out of QRMP at the start of a quarter, subject to GST portal timelines.' },
      { q: 'Is QRMP available for all types of businesses?', a: 'It is available to any registered taxpayer under ₹5 crore turnover, across most business types, opted per GSTIN.' },
      { q: 'What if I miss a monthly payment?', a: 'You will owe interest on the shortfall when you true it up in the quarterly return, so we track and remind you every month.' },
      { q: 'What is the difference between the fixed-sum and self-assessment payment methods?', a: 'Fixed-sum pays 35% of the last quarter\'s cash liability automatically; self-assessment lets you calculate the actual monthly liability yourself — we help you pick whichever suits your cash flow better.' },
    ],
  },

  GST_ANNUAL_RETURN: {
    intro: [
      'GSTR-9 is the annual return that consolidates all your monthly/quarterly filings for the financial year into one comprehensive summary. Businesses above a specified turnover also need GSTR-9C, a reconciliation statement comparing your GST returns with your audited financial statements.',
      'SolveTax reconciles a full year of GST data — sales, purchases, credit notes and input tax credit — against your books and files an accurate annual return on your behalf.',
    ],
    eligibilityTitle: 'Who needs to file the annual return?',
    eligibility: [
      'Most regular taxpayers registered under GST for any part of the financial year',
      'GSTR-9C reconciliation applies once turnover crosses the government-notified threshold',
      'Composition scheme taxpayers file a simplified version (GSTR-9A, where applicable)',
    ],
    documents: [
      'All monthly/quarterly GSTR-1 and GSTR-3B filed during the year',
      'Annual sales and purchase registers',
      'Audited financial statements (for GSTR-9C, where applicable)',
      'Input tax credit ledger and reversal details for the year',
    ],
    benefits: [
      'Consolidated, verified annual position instead of relying on 12 separate monthly filings',
      'Catches and corrects reconciliation gaps before they turn into a departmental notice',
      'Keeps input tax credit claims defensible with a documented annual trail',
    ],
    process: [
      { title: 'Share a year of GST data', desc: 'We pull together all your monthly/quarterly returns and books for the financial year.' },
      { title: 'Full reconciliation', desc: 'Sales, purchases, credit notes and input tax credit are reconciled between your returns and your books.' },
      { title: 'Draft shared for review', desc: 'You review a summary before we finalize anything — differences are flagged and explained.' },
      { title: 'GSTR-9 (and 9C, if applicable) filed', desc: 'Filed on the GST portal with your acknowledgement shared for records.' },
    ],
    timeline: 'The annual return is typically due by 31 December following the end of the financial year, though the government sometimes extends this deadline.',
    mistakes: [
      { title: 'Treating it as a copy-paste of monthly returns', desc: 'GSTR-9 requires genuine reconciliation, not just totaling up monthly figures — differences between books and returns need to be explained, not ignored.' },
      { title: 'Missing the HSN-wise summary details', desc: 'Incomplete HSN/SAC-wise reporting is one of the most common reasons annual returns get flagged for scrutiny later.' },
    ],
    penalties: [
      { title: 'Late filing fee', desc: 'A per-day late fee applies (subject to a cap based on turnover) until the annual return is filed.' },
    ],
    faqs: [
      { q: 'Is GSTR-9 mandatory for every business?', a: 'It is mandatory for most regular taxpayers, though small taxpayers below a government-notified turnover have had exemptions in some years — we confirm your specific requirement.' },
      { q: 'What is the difference between GSTR-9 and GSTR-9C?', a: 'GSTR-9 is the annual summary return; GSTR-9C is a reconciliation statement (with audited financials) required only above a specified turnover threshold.' },
      { q: 'Can errors from monthly returns be corrected in the annual return?', a: 'Certain adjustments and corrections can be made through the annual return, within the limits allowed by GST law.' },
      { q: 'What records do I need to provide?', a: "A full year's sales and purchase registers, all filed monthly/quarterly returns, and your financial statements for reconciliation." },
      { q: 'What if my books and GST returns don\'t match?', a: 'This is exactly what the annual return reconciliation is meant to catch — we identify the gap, document the reason, and reflect it correctly rather than leaving it unexplained.' },
    ],
  },

  GST_AMENDMENT: {
    intro: [
      'GST amendment lets you update details on your existing GST registration — your business address, additional places of business, partner/director details, contact information or business name — without needing a fresh registration.',
      'SolveTax identifies whether your change needs the simpler "core field" amendment or the immediate "non-core field" update, and handles the filing so your GST record stays accurate.',
    ],
    eligibilityTitle: 'When do you need a GST amendment?',
    eligibility: [
      'Business name, address or additional place of business has changed',
      'Partners, directors or authorized signatories have changed',
      'Contact details (mobile/email) need to be updated',
      'Bank account details linked to GST need updating',
    ],
    documents: [
      'Proof supporting the specific change (new rent agreement, board resolution, etc.)',
      'PAN and ID proof of any newly added partner/director/signatory',
      'Updated bank statement or cancelled cheque, if bank details are changing',
    ],
    benefits: [
      'Keeps your GST certificate and invoices accurate and legally consistent',
      'Avoids mismatches that can complicate e-way bill generation or input credit claims for your buyers',
      'Non-core changes are typically reflected the same day, with minimal disruption',
    ],
    process: [
      { title: 'Tell us what changed', desc: 'Share the updated details and supporting proof (address proof, board resolution, etc.).' },
      { title: 'We classify the amendment', desc: 'Core field changes need departmental approval; non-core changes are typically auto-approved.' },
      { title: 'Application filed', desc: 'We submit the amendment (Form REG-14) on the GST portal on your behalf.' },
      { title: 'Confirmation shared', desc: 'You receive confirmation once the amendment reflects on your GST certificate.' },
    ],
    timeline: 'Non-core amendments usually reflect immediately; core field amendments (like address changes) can take 15 working days pending officer approval.',
    mistakes: [
      { title: 'Not updating GST after a business address move', desc: 'Continuing to invoice from a new address without updating GST records can create address-mismatch issues during any future verification.' },
      { title: 'Missing supporting proof for the changed field', desc: 'An amendment filed without matching documentary proof (e.g., new rent agreement for an address change) is a common reason for an officer query.' },
    ],
    faqs: [
      { q: 'What is a "core field" vs "non-core field" amendment?', a: 'Core fields (legal name, principal place of business, addition/deletion of partners) need departmental approval. Non-core fields (like email or mobile number) are usually approved automatically.' },
      { q: 'Can I amend my GST registration myself?', a: 'Yes, but incorrect classification of the amendment type or missing documents is a common reason for delay or rejection — we handle this for accuracy.' },
      { q: 'How many times can I amend my registration?', a: 'There is no fixed limit, but frequent changes to core details may invite departmental scrutiny.' },
      { q: 'Do I need new documents for every amendment?', a: 'Only for the specific field being changed — for example, a new rent agreement for an address change.' },
      { q: 'How soon should I file an amendment after a change happens?', a: 'As soon as possible — GST law requires amendments to be filed within 15 days of the change, and delaying can create invoicing and compliance mismatches.' },
    ],
  },

  GST_CANCELLATION: {
    intro: [
      "GST cancellation formally surrenders your GSTIN when your business closes, turnover falls below the threshold, or you're merging/restructuring your entity. Once cancelled, you must stop collecting GST and file a final return.",
      'SolveTax handles the surrender application and the mandatory final return (GSTR-10), so your GST record is closed out cleanly with no pending liabilities.',
    ],
    eligibilityTitle: 'When should you cancel GST registration?',
    eligibility: [
      'Business has closed, been sold or discontinued',
      'Turnover has permanently fallen below the registration threshold',
      'Change in business constitution (e.g., proprietorship converting to a company)',
      'Voluntary registration is no longer needed',
    ],
    documents: [
      'GSTIN and login credentials for the registration being cancelled',
      'Details of closing stock, if any, held on the cancellation date',
      'Last filed return and outstanding liability details',
      'Board resolution or authorization, for companies/LLPs',
    ],
    benefits: [
      'Formally closes out your compliance obligation instead of leaving a dormant, still-liable GSTIN',
      'Avoids accumulating late fees on a GSTIN you are no longer using',
      'Clean closure supports a smoother re-registration later, if needed',
    ],
    process: [
      { title: 'Confirm your reason for cancellation', desc: 'We check your outstanding returns and liabilities before filing.' },
      { title: 'Application filed', desc: 'Form REG-16 is submitted on the GST portal with the reason and effective date.' },
      { title: 'Final return (GSTR-10) prepared', desc: 'A mandatory final return closing out your GST account is filed within the deadline.' },
      { title: 'Cancellation confirmed', desc: 'You receive the cancellation order once the officer approves the application.' },
    ],
    timeline: 'The final return (GSTR-10) must be filed within 3 months of the cancellation date or the order date, whichever is later.',
    mistakes: [
      { title: 'Forgetting the mandatory final return (GSTR-10)', desc: 'Many assume cancellation is complete once the application is approved — GSTR-10 is a separate, mandatory step with its own deadline and penalty.' },
      { title: 'Not clearing outstanding liability first', desc: 'Pending tax dues or unfiled returns can delay or block your cancellation application from being approved.' },
    ],
    penalties: [
      { title: 'Missing the final return (GSTR-10)', desc: 'A late fee applies per day of delay, and a notice can be issued demanding the pending return.' },
    ],
    faqs: [
      { q: 'Can I still be liable for GST after cancellation?', a: 'Yes — any pending tax dues up to the date of cancellation must still be paid, and the final return (GSTR-10) is mandatory.' },
      { q: 'How long does cancellation take?', a: 'The application itself is usually processed within a few weeks, but the process is only complete once the final return is filed.' },
      { q: 'Can a cancelled GSTIN be reactivated?', a: 'In specific cases, revocation of cancellation can be applied for within the timeline allowed by law — SolveTax can assist with this separately.' },
      { q: 'Do I need to cancel GST if I stop trading temporarily?', a: 'Not necessarily — if you expect to resume, you may prefer to continue filing nil returns rather than cancel and re-register later.' },
      { q: 'What happens to my closing stock at cancellation?', a: 'You may need to pay tax on the input tax credit related to closing stock/capital goods held as of the cancellation date — we calculate this correctly as part of GSTR-10.' },
    ],
  },

  GST_LUT: {
    intro: [
      "A Letter of Undertaking (LUT) lets exporters supply goods or services without paying IGST upfront, instead of paying tax and claiming a refund later. It's renewed every financial year and is essential for exporters who want smoother cash flow.",
      'SolveTax files your LUT (Form GST RFD-11) at the start of the financial year so your export invoices stay IGST-free from day one.',
    ],
    eligibilityTitle: 'Who should file an LUT?',
    eligibility: [
      'Exporters of goods or services who want to avoid blocking working capital in IGST',
      'Businesses supplying to SEZ units or developers without payment of tax',
      'Any registered taxpayer not prosecuted for tax evasion above a specified amount',
    ],
    documents: [
      'GSTIN and business PAN',
      'Details of two independent witnesses (name, address, occupation)',
      'Authorized signatory\'s KYC and digital signature/Aadhaar e-sign',
      'Prior year LUT acknowledgement, if renewing',
    ],
    benefits: [
      'Export without paying IGST upfront — better cash flow',
      'Avoid the refund-claim process for IGST paid on exports',
      'Valid for the entire financial year once approved',
      'Simple, fully online filing with no fee',
    ],
    process: [
      { title: 'Eligibility confirmed', desc: 'We check that your GSTIN qualifies (no prosecution for tax evasion above the specified threshold).' },
      { title: 'Form RFD-11 prepared', desc: 'The LUT application is prepared with witness details and authorized signatory information.' },
      { title: 'Filed online', desc: 'Submitted on the GST portal using DSC or Aadhaar e-signature.' },
      { title: 'Acknowledgement issued', desc: 'An LUT reference number (ARN) is generated instantly, valid for the financial year.' },
    ],
    timeline: 'LUT should ideally be filed at the start of the financial year (before the first export invoice) — approval is typically instant to a few days.',
    mistakes: [
      { title: 'Exporting before the LUT is actually filed', desc: 'Assuming last year\'s LUT carries forward automatically — it doesn\'t; a fresh LUT is required every financial year before the first export invoice.' },
      { title: 'Incorrect witness details', desc: 'Incomplete or mismatched witness information is a common reason the application gets sent back for correction.' },
    ],
    faqs: [
      { q: 'Is LUT mandatory for exporters?', a: 'Not mandatory — exporters can instead pay IGST and claim a refund. LUT is the preferred option for most because it avoids blocking working capital.' },
      { q: 'How often does the LUT need renewal?', a: 'Every financial year — a fresh LUT must be filed before the first export of that year.' },
      { q: 'What happens if I export before my LUT is approved?', a: 'You would need to pay IGST on those exports and claim a refund, since the LUT was not yet in effect.' },
      { q: 'Is there a fee for filing LUT?', a: 'No government fee is charged for filing an LUT on the GST portal.' },
      { q: 'Can a new exporter with no prior track record file an LUT?', a: 'Yes, LUT eligibility is based on not being prosecuted for tax evasion above the specified amount, not on export history — first-time exporters can apply.' },
    ],
  },

  GST_REFUND: {
    intro: [
      'GST refunds arise when you have accumulated more input tax credit than your output tax liability — commonly for exporters, businesses with an inverted duty structure, or excess tax paid by mistake. Claiming refunds correctly requires precise documentation and reconciliation.',
      'SolveTax prepares and files your refund application (RFD-01) with the supporting statement of invoices, and follows up with the department until the refund is credited.',
    ],
    eligibilityTitle: 'Common refund scenarios',
    eligibility: [
      'Exporters with accumulated input tax credit (with or without LUT)',
      'Inverted duty structure — tax on inputs higher than tax on output supplies',
      'Excess balance in electronic cash ledger',
      'Tax paid by mistake or on account of an assessment/appeal order',
    ],
    documents: [
      'Shipping bills / export invoices (for export refunds)',
      'Bank realization certificate (BRC/FIRC) for service exports',
      'GSTR-2B and purchase invoice details supporting the ITC claim',
      'Statement of relevant invoices in the prescribed refund format',
    ],
    benefits: [
      'Unblocks working capital tied up in excess input tax credit',
      'Provisional refund of up to 90% available to certain exporters, ahead of full processing',
      'Statutory interest is payable to you if the department delays beyond 60 days',
    ],
    process: [
      { title: 'Reconcile your eligible credit', desc: 'We review your GSTR-2B, export invoices and shipping bills to determine the exact refund amount.' },
      { title: 'Application prepared', desc: 'Form RFD-01 is prepared with the statement of invoices and supporting documents.' },
      { title: 'Filed & acknowledged', desc: 'The application is filed online and an Application Reference Number is generated.' },
      { title: 'Follow-up until credited', desc: 'We track the application and respond to any department queries until the refund is processed.' },
    ],
    timeline: 'The department is required to process refund applications within 60 days of a complete application, though straightforward cases are often faster.',
    mistakes: [
      { title: 'Filing with mismatched invoice details', desc: 'Invoice numbers or values in the refund statement that don\'t exactly match GSTR-1/GSTR-2B are the most common reason for a deficiency memo.' },
      { title: 'Missing the 2-year limitation period', desc: 'Refund claims must generally be filed within 2 years of the relevant date — delaying can mean losing the claim entirely.' },
    ],
    faqs: [
      { q: 'How long does a GST refund take?', a: 'The law requires processing within 60 days of a complete application; provisional refund of 90% may be granted to certain exporters even faster.' },
      { q: 'What documents are needed for an export refund?', a: 'Shipping bills, export invoices, bank realization certificates (for services) and your GSTR filings for the relevant period.' },
      { q: 'Can a refund claim be rejected?', a: 'Yes, common reasons include mismatched invoices, missing documents or ineligible credit — we reconcile carefully upfront to avoid this.' },
      { q: 'Is there interest if my refund is delayed?', a: 'Yes, interest is payable by the department if the refund is not processed within the statutory 60-day window.' },
      { q: 'What is the deadline to claim a GST refund?', a: 'Generally within 2 years from the "relevant date" (varies by refund type, e.g. date of export or date of tax payment) — we confirm your specific deadline.' },
    ],
  },

  GST_NOTICE_REPLY: {
    intro: [
      'GST notices — from a simple ASMT-10 scrutiny notice to a DRC-01 show-cause notice for demand — need a timely, well-documented reply to avoid escalation to penalty proceedings. Ignoring or mishandling a notice is far more costly than a professionally prepared response.',
      'SolveTax reviews the notice, identifies exactly what the department is asking, and drafts a reply backed by your reconciled records — filed within the deadline given.',
    ],
    eligibilityTitle: 'Common types of GST notices',
    eligibility: [
      'ASMT-10 — scrutiny of discrepancies in returns filed',
      'DRC-01 / DRC-01A — show-cause notice for tax demand',
      'REG-17 — show-cause notice proposing cancellation of registration',
      'GSTR-3A — notice for non-filing of returns',
    ],
    documents: [
      'Copy of the notice received',
      'All GST returns filed for the relevant period',
      'Books of account, invoices and GSTR-2B for the disputed period',
      'Any previous correspondence with the department on the same matter',
    ],
    benefits: [
      'A timely, well-documented reply resolves most notices without escalation',
      'Avoids an ex-parte order being passed purely due to non-response',
      'Reduces the chance of the issue recurring in future periods once properly clarified',
    ],
    process: [
      { title: 'Share the notice', desc: 'Send us the notice as soon as you receive it — deadlines are often short (7–30 days).' },
      { title: 'We assess the discrepancy', desc: 'Your CA reviews the notice against your filed returns and books to understand the exact gap being questioned.' },
      { title: 'Reply drafted', desc: 'A point-by-point, documented reply is prepared, with reconciliation statements where needed.' },
      { title: 'Filed within deadline', desc: 'The reply is submitted on the GST portal before the due date, with acknowledgement shared with you.' },
    ],
    timeline: 'Reply deadlines vary by notice type, typically 7–30 days from the date of issue — we prioritize these cases given the tight timelines.',
    mistakes: [
      { title: 'Ignoring a notice that seems minor', desc: 'Even a routine scrutiny notice (ASMT-10) needs a formal reply — silence can be read as agreement with the discrepancy raised.' },
      { title: 'Replying without full reconciliation', desc: 'A reply that doesn\'t address the specific figures the department is questioning often triggers a follow-up notice instead of closing the matter.' },
    ],
    penalties: [
      { title: 'No reply filed', desc: 'Can lead to an ex-parte order confirming the demand, along with penalty and interest, or cancellation of registration.' },
    ],
    faqs: [
      { q: 'What should I do the moment I get a GST notice?', a: 'Note the deadline and reply requirement, and share it with your CA immediately — most notices have short response windows.' },
      { q: 'Can a wrongly issued notice be contested?', a: 'Yes, a well-reasoned reply with supporting reconciliation can clarify or contest a notice issued due to a data mismatch or clerical error.' },
      { q: 'What if I miss the reply deadline?', a: 'The department can pass an order based on available information, which is harder and more costly to contest afterward — always aim to reply within the deadline.' },
      { q: 'Do all notices lead to a penalty?', a: 'No — many notices (like ASMT-10) are simply seeking clarification and are closed once a satisfactory reply is filed.' },
      { q: 'Can I request more time to respond to a GST notice?', a: 'In some cases a short adjournment can be requested from the officer, but it is not guaranteed — it is safer to prepare and file the reply within the original deadline.' },
    ],
  },

  // ===========================================================================
  // INCOME TAX
  // ===========================================================================
  ITR_FILING: {
    intro: [
      'Filing your Income Tax Return (ITR) is how you formally report your income, claim eligible deductions and either pay balance tax or claim a refund for a financial year. The right form (ITR-1 through ITR-7) depends on your income sources, and errors can trigger notices or delay your refund.',
      'SolveTax matches you with the correct ITR form based on your salary, business, capital gains or multiple income sources, applies every deduction you are eligible for, and files electronically with e-verification.',
    ],
    eligibilityTitle: 'Who should file an ITR?',
    eligibility: [
      'Anyone whose income exceeds the basic exemption limit for their age category',
      'Individuals wanting to claim a TDS refund deducted by an employer or bank',
      'Those with foreign assets/income, or holding directorship in a company',
      'Freelancers, professionals and business owners regardless of profit level',
      'Anyone applying for a visa or loan, where ITR is commonly required as proof of income',
    ],
    benefits: [
      'Claim refunds on excess TDS deducted during the year',
      'Carry forward capital losses to offset against future gains',
      'Serves as verified income proof for loans, credit cards and visas',
      'Avoid late filing fees and interest by filing on time',
      'Build a clean compliance record, reducing scrutiny risk over time',
    ],
    documents: [
      'Form 16 (if salaried) and salary slips',
      'Bank statements for the financial year',
      'Investment proofs for deductions (80C, 80D, home loan interest, etc.)',
      'Capital gains statements from broker/mutual fund platforms, if applicable',
      'PAN, Aadhaar and bank account details for refund credit',
    ],
    process: [
      { title: 'Share your income details', desc: 'Fill the form with your income sources — salary, business, capital gains or rental income.' },
      { title: 'A dedicated CA calls you', desc: 'Our expert calls within 2 hours to understand your case and request the right documents.' },
      { title: 'We prepare your return', desc: 'Form 16, AIS/TIS, bank statements and investment proofs are reconciled to maximize eligible deductions.' },
      { title: 'You review before filing', desc: 'We share the computed tax/refund summary with you before submitting anything.' },
      { title: 'Filed & e-verified', desc: 'Your ITR is filed and e-verified — you get the acknowledgement (ITR-V) for your records.' },
    ],
    timeline: 'The standard due date for individuals (non-audit cases) is 31 July following the financial year end; audit cases have a later deadline. We recommend starting early to avoid the last-minute rush.',
    mistakes: [
      { title: 'Choosing the wrong ITR form', desc: 'Filing ITR-1 when you actually have capital gains or multiple properties makes the return defective and can trigger a correction notice.' },
      { title: 'Not reconciling with Form 26AS / AIS', desc: 'Skipping this check means missing TDS credit already deducted, or under-reporting income the department can already see on your AIS.' },
      { title: 'Forgetting to e-verify after filing', desc: 'An ITR is treated as not filed at all if it isn\'t e-verified within the given window — filing alone is not the final step.' },
    ],
    penalties: [
      { title: 'Late filing fee (Section 234F)', desc: 'Up to ₹5,000 for filing after the due date (₹1,000 if total income is below ₹5 lakh).' },
      { title: 'Interest on unpaid tax', desc: '1% per month under Sections 234A/B/C on any tax that remains unpaid past the due date.' },
      { title: 'Loss of carry-forward benefit', desc: 'Certain losses (like capital losses) cannot be carried forward if the return is filed after the due date.' },
    ],
    faqs: [
      { q: 'Which ITR form applies to me?', a: 'It depends on your income sources — ITR-1 for simple salary income, ITR-2 for capital gains/multiple properties, ITR-3/4 for business and professional income, and so on. We determine the correct form for you.' },
      { q: 'Do I need to file ITR if my employer already deducted TDS?', a: 'Yes — TDS is not a substitute for filing. You still need to file to reconcile your actual tax liability and claim any refund due.' },
      { q: 'What documents do I need?', a: 'Form 16 (if salaried), bank statements, investment proofs for deductions, capital gains statements, and your PAN and Aadhaar.' },
      { q: 'How long does it take to get a refund?', a: 'Refunds are typically credited within 4–6 weeks of e-verification, though it can vary based on department processing.' },
      { q: 'What if I have income from multiple sources?', a: 'That is exactly where a CA-prepared return helps — we consolidate salary, business, capital gains and other income correctly into one accurate filing.' },
      { q: 'Can I file a belated return after the due date?', a: 'Yes, a belated return can be filed with a late fee, up to the timeline allowed under the Income Tax Act for that assessment year.' },
    ],
  },

  ITR_NOTICE: {
    intro: [
      'Income Tax notices — from a routine 143(1) intimation to a 148 reassessment or 139(9) defective return notice — need a precise, well-documented response within the given deadline. The right reply can resolve most notices without further escalation.',
      "SolveTax reviews your notice, cross-checks it against your filed return and AIS/TIS data, and drafts a response that directly addresses what the department is asking.",
    ],
    eligibilityTitle: 'Common types of Income Tax notices',
    eligibility: [
      'Section 139(9) — defective return notice requiring correction',
      'Section 143(1) — intimation showing a computation mismatch',
      'Section 143(2) — scrutiny assessment notice',
      'Section 148 — reassessment notice for income believed to have escaped assessment',
      'Section 245 — adjustment of refund against past demand',
    ],
    documents: [
      'Copy of the notice received (with the DIN/notice number)',
      'Filed ITR and acknowledgement for the relevant assessment year',
      'Form 26AS, AIS and TIS for the relevant year',
      'Supporting documents for the specific income/deduction being questioned',
    ],
    benefits: [
      'A precise, documented reply resolves most notices without further escalation',
      'Avoids a best-judgment assessment being passed due to non-response',
      'Protects your refund from being withheld or adjusted incorrectly',
    ],
    process: [
      { title: 'Share the notice', desc: 'Send us the notice as soon as you receive it, along with your filed return for that year.' },
      { title: 'We diagnose the issue', desc: 'Your CA compares the notice against your return, Form 26AS and AIS to identify the exact discrepancy.' },
      { title: 'Response drafted', desc: 'A documented reply is prepared — correcting, clarifying or contesting the notice as appropriate.' },
      { title: 'Filed within deadline', desc: 'Submitted through the income tax portal before the due date, with acknowledgement shared with you.' },
    ],
    timeline: 'Response deadlines vary by notice type — often 15–30 days from the date of issue. We treat these as priority given the tight windows.',
    mistakes: [
      { title: 'Assuming the notice will "go away" if ignored', desc: 'Every notice type has a specific deadline; missing it can lead to an order being passed without your side being considered.' },
      { title: 'Replying without checking AIS/TIS first', desc: 'Many notices stem from a mismatch with data the department already has on your AIS — not reconciling against it before replying often leads to a follow-up notice.' },
    ],
    penalties: [
      { title: 'Best-judgment assessment', desc: 'If you don\'t respond, the assessing officer can pass an order based on available information, which is often less favorable than your actual position.' },
      { title: 'Penalty for concealment (where applicable)', desc: 'If the notice relates to under-reported or misreported income, a penalty of 50–200% of the tax on the under-reported amount can apply under Section 270A.' },
    ],
    faqs: [
      { q: 'I got a notice — should I panic?', a: 'No. Most notices, especially 143(1) intimations, are routine and simply need a correction or clarification, not a dispute.' },
      { q: 'What is a defective return notice (139(9))?', a: 'It means your filed return has an error or missing information — like an unmatched TDS claim — that needs to be corrected within the given time to keep your original filing date.' },
      { q: 'Can I ignore a notice if I think it is wrong?', a: 'No — even an incorrect notice needs a documented reply; ignoring it can lead to an unfavorable order being passed automatically.' },
      { q: 'What happens if I miss the response deadline?', a: 'The department may proceed based on available information, which is harder to reverse than replying on time.' },
      { q: 'What is a Section 148 reassessment notice?', a: 'It is issued when the department believes some income has escaped assessment in a past year — it needs a careful, well-documented response since it can reopen an earlier filed return.' },
    ],
  },

  ADVANCE_TAX: {
    intro: [
      "Advance tax is income tax paid in installments during the financial year — as you earn — rather than as one lump sum at filing time. It applies once your estimated annual tax liability exceeds ₹10,000, and missing installments attracts interest even if you pay the full amount before filing.",
      'SolveTax estimates your quarterly liability based on your income pattern and reminds and helps you pay each installment on time, avoiding interest under Sections 234B and 234C.',
    ],
    eligibilityTitle: 'Who needs to pay advance tax?',
    eligibility: [
      'Freelancers, professionals and business owners with tax liability above ₹10,000 for the year',
      'Salaried individuals with significant additional income (capital gains, rental, freelance)',
      'Senior citizens are exempt if they do not have business income',
    ],
    documents: [
      'Estimated income from all sources for the financial year',
      'Details of TDS already deducted so far in the year',
      'Previous year\'s ITR, for reference on income pattern',
      'Bank account details for making the challan payment',
    ],
    benefits: [
      'Avoids a large lump-sum tax burden at filing time',
      'Prevents interest under Sections 234B and 234C entirely when paid on schedule',
      'Gives a running, realistic picture of your tax liability through the year',
    ],
    table: {
      title: 'Advance tax payment schedule',
      columns: ['Due date', 'Cumulative % of tax due'],
      rows: [
        ['By 15 June', '15%'],
        ['By 15 September', '45%'],
        ['By 15 December', '75%'],
        ['By 15 March', '100%'],
      ],
    },
    process: [
      { title: 'Share your income estimate', desc: 'Tell us your expected income for the year from all sources.' },
      { title: 'We compute your liability', desc: 'Your CA calculates the quarterly advance tax due based on projected income and eligible deductions.' },
      { title: 'Reminders each quarter', desc: 'We notify you before each due date with the exact amount payable.' },
      { title: 'Payment & challan', desc: 'We guide you through payment and keep the challan on record for your annual filing.' },
    ],
    timeline: 'Four installments across the year — 15 June, 15 September, 15 December and 15 March — see the payment schedule table above.',
    mistakes: [
      { title: 'Underestimating a one-off capital gain', desc: 'A large one-time gain (property sale, ESOP sale) mid-year is often missed from the estimate, creating a sudden shortfall and Section 234C interest for that quarter.' },
      { title: 'Waiting until March to pay everything', desc: 'Paying the full year\'s tax only in the last installment still attracts interest on the earlier missed installments, even though the annual total is met on time.' },
    ],
    penalties: [
      { title: 'Interest under Section 234B', desc: '1% per month if advance tax paid is less than 90% of the total tax liability for the year.' },
      { title: 'Interest under Section 234C', desc: '1% per month for shortfall in any individual quarterly installment, even if the annual total is eventually met.' },
    ],
    faqs: [
      { q: 'Do I need to pay advance tax if my employer deducts TDS?', a: 'If TDS already covers most of your liability you may not owe much extra, but any additional income (capital gains, freelance, rental) not covered by TDS can still trigger an advance tax obligation.' },
      { q: 'What if my income estimate changes during the year?', a: 'You can revise your remaining installments — advance tax is based on your best estimate at each due date, adjusted as your year progresses.' },
      { q: 'Is advance tax applicable to businesses?', a: 'Yes, businesses and professionals with tax liability above ₹10,000 must pay advance tax in the same quarterly schedule.' },
      { q: 'What if I miss all four installments?', a: 'Interest accrues under Sections 234B and 234C from the respective due dates until the tax is paid, in addition to your regular filing.' },
    ],
  },

  CAPITAL_GAINS: {
    intro: [
      'Capital gains tax applies when you sell shares, mutual funds, property or other capital assets at a profit. The rate and computation differ significantly by asset type and holding period (short-term vs long-term), and getting this wrong is one of the most common causes of an income tax notice.',
      'SolveTax computes your capital gains accurately across stocks, mutual funds and property — applying indexation, exemptions (like Section 54) and set-off of losses correctly — and reflects it in your ITR.',
    ],
    eligibilityTitle: 'When do you need capital gains computation?',
    eligibility: [
      "You've sold listed shares or equity mutual funds during the year",
      "You've sold property, land or an unlisted investment",
      'You want to claim exemption on capital gains by reinvesting (Section 54/54F/54EC)',
      'You have capital losses to carry forward or set off against gains',
    ],
    documents: [
      'Broker/mutual fund capital gains statement (for shares and funds)',
      'Sale deed and purchase deed with stamp duty value (for property)',
      'Details of reinvestment, if claiming Section 54/54F/54EC exemption',
      'Details of prior years\' carried-forward capital losses, if any',
    ],
    benefits: [
      'Correct classification of short-term vs long-term gains, taxed at different rates',
      'Indexation benefit applied correctly for eligible long-term assets',
      'Capital losses set off or carried forward to reduce future tax',
      'Exemptions under Sections 54, 54F and 54EC claimed where eligible',
    ],
    process: [
      { title: 'Share your transaction statements', desc: 'Broker/mutual fund statements for shares, or sale deed and purchase details for property.' },
      { title: 'We compute gains accurately', desc: 'Your CA classifies each transaction, applies indexation where applicable and checks for eligible exemptions.' },
      { title: 'Optimization check', desc: 'We check whether reinvestment exemptions or loss set-offs can reduce your tax outgo.' },
      { title: 'Included in your ITR', desc: 'The computation is reflected correctly in your income tax return filing.' },
    ],
    timeline: 'Capital gains computation is completed alongside your regular ITR filing, well before the 31 July due date for non-audit cases.',
    mistakes: [
      { title: 'Missing small/frequent transactions', desc: 'Forgetting to report smaller share or mutual fund redemptions because they seem too minor — the department can still see them via AIS/TIS.' },
      { title: 'Claiming exemption without meeting the reinvestment timeline', desc: 'Sections 54/54F/54EC have strict reinvestment windows — claiming the exemption without meeting them can lead to it being disallowed later with interest.' },
      { title: 'Not carrying forward eligible losses', desc: 'Capital losses not reported in a return filed on time cannot be carried forward at all, permanently losing a valid tax benefit.' },
    ],
    faqs: [
      { q: 'What is the difference between short-term and long-term capital gains?', a: 'It depends on how long you held the asset before selling — for listed shares/equity funds, over 12 months is long-term; for property, over 24 months. Each is taxed differently.' },
      { q: 'Can I reduce my capital gains tax on property?', a: 'Yes, exemptions like Section 54 (reinvesting in a residential property) or 54EC (investing in specified bonds) can reduce or eliminate the tax, subject to conditions.' },
      { q: 'Do I need to report capital gains even if I reinvested the proceeds?', a: 'Yes, the transaction and the exemption claimed both need to be reported in your ITR, even if no tax is ultimately payable.' },
      { q: 'Can capital losses be carried forward?', a: 'Yes, short-term and long-term capital losses can be carried forward for 8 assessment years to set off against future capital gains, provided the return is filed on time.' },
    ],
  },

  // ===========================================================================
  // COMPANY REGISTRATION
  // ===========================================================================
  PVT_LTD_REG: {
    intro: [
      'A Private Limited Company is the most common structure for startups and growing businesses in India — offering limited liability, easier fundraising and a professional structure that investors and larger clients trust.',
      'SolveTax handles the entire incorporation — name approval, DSC and DIN, drafting MOA/AOA, and filing with the MCA — so your company is ready to operate with minimal back-and-forth.',
    ],
    eligibilityTitle: 'Who should register a Private Limited Company?',
    eligibility: [
      'Founders planning to raise funding from investors or venture capital',
      'Businesses wanting limited liability protection for owners',
      'At least 2 directors and 2 shareholders (can be the same people), with at least one resident Indian director',
      'Businesses wanting a more credible structure for larger contracts and clients',
    ],
    benefits: [
      'Limited liability — personal assets are protected from business debts',
      'Easier to raise equity funding from investors and venture capital',
      'Separate legal entity — the company can own assets and enter contracts in its own name',
      'Perpetual succession — continues to exist regardless of changes in ownership',
      'Greater credibility with banks, vendors and enterprise clients',
    ],
    documents: [
      'PAN and Aadhaar of all directors and shareholders',
      'Passport-size photographs of all directors',
      'Proof of registered office address (electricity bill + rent agreement/NOC)',
      'Digital Signature Certificate (DSC) for all directors',
    ],
    process: [
      { title: 'Name reservation', desc: 'We check availability and reserve your company name via the MCA RUN service.' },
      { title: 'DSC & DIN', desc: 'Digital Signature Certificates and Director Identification Numbers are obtained for all directors.' },
      { title: 'Drafting MOA & AOA', desc: 'Your Memorandum and Articles of Association are drafted to reflect your business objectives.' },
      { title: 'SPICe+ filing', desc: 'The integrated incorporation form is filed with the MCA, along with PAN, TAN and bank account requests.' },
      { title: 'Certificate of Incorporation', desc: 'Once approved, you receive your CIN, incorporation certificate, PAN and TAN.' },
    ],
    timeline: 'Most Private Limited incorporations are completed within 7–15 working days, depending on name approval and document readiness.',
    mistakes: [
      { title: 'Choosing a name too similar to an existing company/trademark', desc: 'Name rejection at the MCA RUN stage is one of the most common causes of delay — we check availability against both company records and trademarks first.' },
      { title: 'Underestimating post-incorporation compliance', desc: 'Founders sometimes assume incorporation is the finish line — annual ROC filings, DIR-3 KYC and statutory audit obligations start from year one regardless of revenue.' },
    ],
    penalties: [
      { title: 'Late annual ROC filing', desc: 'An additional per-day government fee applies to AOC-4/MGT-7 filed after the deadline, on top of the risk of director disqualification for repeated defaults.' },
    ],
    faqs: [
      { q: 'How many directors and shareholders do I need?', a: 'A minimum of 2 directors and 2 shareholders is required (the same 2 people can hold both roles), with at least one director being a resident Indian.' },
      { q: 'What is the minimum capital required?', a: 'There is no minimum paid-up capital requirement — you can start with any amount you choose to declare.' },
      { q: "What's included in incorporation — do I get PAN and a bank account too?", a: 'Yes, PAN and TAN are issued as part of the SPICe+ process, and we help you complete the current account opening with your bank of choice.' },
      { q: 'What compliance is required after incorporation?', a: 'Ongoing requirements include annual ROC filings (AOC-4, MGT-7), DIR-3 KYC for directors, income tax filing and, if applicable, GST registration.' },
      { q: "Can NRIs or foreign nationals be directors?", a: 'Yes, subject to at least one director being a resident Indian and compliance with FDI/FEMA regulations where foreign shareholding is involved.' },
    ],
  },

  LLP_REG: {
    intro: [
      'A Limited Liability Partnership (LLP) combines the flexibility of a partnership with the limited liability protection of a company — a popular choice for professional services firms and small businesses that want lower compliance than a Pvt Ltd company.',
      'SolveTax handles your LLP incorporation end-to-end — DSC, DPIN, name reservation and the LLP agreement — so you can start operating quickly.',
    ],
    eligibilityTitle: 'Who should register an LLP?',
    eligibility: [
      'Professional services firms (consultants, CAs, designers, agencies)',
      'Businesses wanting limited liability without heavy corporate compliance',
      'At least 2 designated partners, with at least one resident in India',
      'Businesses not currently planning to raise equity/VC funding (LLPs cannot issue shares)',
    ],
    documents: [
      'PAN and Aadhaar of all designated partners',
      'Passport-size photographs of all partners',
      'Proof of registered office address (electricity bill + rent agreement/NOC)',
      'Digital Signature Certificate for at least one designated partner',
    ],
    benefits: [
      'Limited liability protection for all partners',
      'Lower compliance burden and cost compared to a Private Limited Company',
      'No minimum capital requirement',
      'Separate legal entity that can own property and enter contracts',
      'Flexible internal management structure via the LLP agreement',
    ],
    process: [
      { title: 'DSC & DPIN', desc: 'Digital Signature Certificates and Designated Partner Identification Numbers are obtained for partners.' },
      { title: 'Name reservation', desc: 'We reserve your LLP name via the RUN-LLP service on the MCA portal.' },
      { title: 'Incorporation filing', desc: 'FiLLiP form filed with the Registrar along with subscriber and partner details.' },
      { title: 'LLP Agreement filed', desc: 'The LLP agreement (Form 3) defining partner roles and profit-sharing is filed within 30 days of incorporation.' },
    ],
    timeline: 'LLP incorporation typically takes 7–12 working days from document submission, subject to name and form approval.',
    mistakes: [
      { title: 'Delaying the LLP Agreement filing', desc: 'Form 3 has its own 30-day deadline separate from incorporation itself — missing it attracts an additional government fee that grows the longer it\'s pending.' },
      { title: 'Assuming an LLP can raise VC funding like a company', desc: 'LLPs cannot issue equity shares or ESOPs — this becomes a costly realization mid-fundraise if not planned for at the structuring stage.' },
    ],
    penalties: [
      { title: 'Late LLP Agreement (Form 3) filing', desc: 'An additional government fee accrues per day of delay beyond the 30-day window.' },
      { title: 'Late annual filing (Form 8/Form 11)', desc: 'A fixed additional fee per day of delay applies, and continued non-filing can lead to the LLP being marked as a defaulter.' },
    ],
    faqs: [
      { q: 'How is an LLP different from a Private Limited Company?', a: 'An LLP has lower compliance and no concept of shares, making it simpler to run, but it cannot raise equity funding the way a Pvt Ltd company can — a key consideration if you plan to raise capital.' },
      { q: 'How many partners does an LLP need?', a: 'A minimum of 2 designated partners is required, with at least one being a resident of India.' },
      { q: 'Is an LLP agreement mandatory?', a: 'Yes — it must be filed within 30 days of incorporation and defines how profits, roles and decision-making are handled among partners.' },
      { q: 'What ongoing compliance does an LLP have?', a: 'Annual filing of Form 8 (statement of accounts) and Form 11 (annual return), plus income tax filing — generally lighter than a Pvt Ltd company\'s requirements.' },
      { q: 'Can an LLP later convert to a Private Limited Company?', a: 'Yes, an LLP can be converted to a Private Limited Company later if you decide to raise equity funding, following a defined conversion process with the ROC.' },
    ],
  },

  OPC_REG: {
    intro: [
      'A One Person Company (OPC) lets a single founder enjoy the limited liability and separate legal identity of a company, without needing a second shareholder like a Private Limited Company requires.',
      "SolveTax handles your OPC incorporation — including the mandatory nominee appointment — so you get a company structure without needing a co-founder.",
    ],
    eligibilityTitle: 'Who should register an OPC?',
    eligibility: [
      'Solo founders who want limited liability without bringing in a second shareholder',
      'Must be an Indian citizen and resident to be eligible as the sole member',
      'One nominee must be appointed in case the sole member becomes incapacitated',
      'Not suitable if you plan to raise external equity funding (OPCs have restrictions here)',
    ],
    documents: [
      'PAN and Aadhaar of the sole member and nominee',
      'Passport-size photograph of the member and nominee',
      'Proof of registered office address (electricity bill + rent agreement/NOC)',
      'Nominee\'s consent in Form INC-3',
    ],
    benefits: [
      'Limited liability protection with just one member',
      'Separate legal entity — no need for a second shareholder',
      'Full control remains with the single founder',
      'Easier to convert to a Private Limited Company later as you scale',
    ],
    process: [
      { title: 'Nominee consent', desc: 'A nominee is identified and their consent (Form INC-3) is obtained.' },
      { title: 'DSC & name reservation', desc: 'Digital Signature Certificate obtained and company name reserved with the MCA.' },
      { title: 'SPICe+ incorporation filing', desc: 'Incorporation form filed along with MOA, AOA and nominee details.' },
      { title: 'Certificate of Incorporation', desc: 'You receive your CIN, incorporation certificate, PAN and TAN once approved.' },
    ],
    timeline: 'OPC incorporation typically takes 7–15 working days, similar to a Private Limited Company.',
    mistakes: [
      { title: 'Not planning for the mandatory conversion trigger', desc: 'Founders sometimes don\'t track paid-up capital/turnover thresholds — crossing them requires mandatory conversion to a Private Limited Company within a set timeline.' },
      { title: 'Choosing a nominee without their full understanding', desc: 'The nominee\'s consent is a real legal commitment — choosing someone unfamiliar with the responsibility can create complications later.' },
    ],
    faqs: [
      { q: 'Can I convert my OPC to a Private Limited Company later?', a: 'Yes, an OPC can be converted voluntarily, and must be converted if paid-up capital or turnover crosses specified thresholds.' },
      { q: 'Who can be the nominee?', a: 'Any Indian citizen and resident willing to take over the company if the sole member is unable to continue — their consent is mandatory at incorporation.' },
      { q: 'Can an OPC have more than one director?', a: 'Yes — an OPC can appoint additional directors for management purposes, but there is still only one shareholder/member.' },
      { q: 'Are there restrictions on OPC business activities?', a: 'OPCs cannot carry out non-banking financial investment activities, and cannot be converted from a company already carrying out such activities.' },
    ],
  },

  PARTNERSHIP_REG: {
    intro: [
      "A partnership firm is a simple, low-cost way for two or more people to run a business together, governed by the Indian Partnership Act. It doesn't offer limited liability, but it is quick to set up and has minimal ongoing compliance.",
      'SolveTax drafts your partnership deed and handles registration with the Registrar of Firms, so your partnership is properly documented and legally recognized.',
    ],
    eligibilityTitle: 'Who should register a partnership firm?',
    eligibility: [
      'Two or more people starting a business together on a smaller scale',
      'Businesses that prioritize simplicity and low compliance over limited liability',
      'Family businesses or professional practices with a small number of partners',
    ],
    documents: [
      'PAN and Aadhaar of all partners',
      'Proof of business address (electricity bill + rent agreement/NOC)',
      'Passport-size photographs of all partners',
      'Signed partnership deed on appropriate stamp paper',
    ],
    benefits: [
      'Quick and inexpensive to set up compared to a company or LLP',
      'Minimal ongoing compliance requirements',
      'Flexible profit-sharing and management as defined in the partnership deed',
      'Registration (though optional) gives the firm legal standing to sue and enforce contracts',
    ],
    process: [
      { title: 'Partnership deed drafted', desc: 'We draft a deed covering capital contribution, profit-sharing and roles of each partner.' },
      { title: 'Application filed', desc: 'Filed with the Registrar of Firms in your state, along with the deed and partner KYC.' },
      { title: 'Registration certificate issued', desc: 'Once approved, you receive the Certificate of Registration for your firm.' },
    ],
    timeline: 'Registration typically takes 7–10 working days, though it varies by state Registrar of Firms.',
    mistakes: [
      { title: 'Operating without a written deed', desc: 'Relying on a verbal understanding between partners is a common source of disputes over profit-sharing and exit terms later.' },
      { title: 'Skipping registration to save cost', desc: 'An unregistered firm cannot sue a third party to enforce a contract — a real limitation that only surfaces when a dispute actually happens.' },
    ],
    faqs: [
      { q: 'Is partnership registration mandatory?', a: 'It is not legally mandatory, but an unregistered firm cannot sue third parties to enforce a contract — registration is strongly recommended for this reason.' },
      { q: 'Do partners have limited liability?', a: 'No — partners have unlimited personal liability for the debts of the firm, which is the main trade-off compared to an LLP or company.' },
      { q: 'Can a partnership firm later convert to an LLP or company?', a: 'Yes, partnership firms can be converted to an LLP or a Private Limited Company as the business grows.' },
      { q: 'What does the partnership deed need to include?', a: "Capital contribution, profit and loss sharing ratio, roles and responsibilities, and provisions for admitting or removing partners." },
    ],
  },

  // ===========================================================================
  // MCA / ROC
  // ===========================================================================
  ROC_ANNUAL: {
    intro: [
      'Every registered company must file annual returns with the Registrar of Companies (ROC) — Form AOC-4 (financial statements) and Form MGT-7 (annual return) — regardless of turnover or whether the company did any business during the year.',
      'SolveTax prepares your financial statements, board resolutions and annual return, and files both forms before the deadline to keep your company compliant.',
    ],
    eligibilityTitle: 'Who needs to file ROC annual returns?',
    eligibility: [
      'Every Private Limited Company, OPC and Public Company registered with the MCA',
      'Applies even to companies with zero turnover or no active business during the year',
      'Due within specific timelines after your Annual General Meeting (AGM)',
    ],
    documents: [
      'Audited financial statements (balance sheet, P&L, notes)',
      'Board report and auditor\'s report',
      'Details of shareholding pattern and any changes during the year',
      'DIN and details of all directors as of the AGM date',
    ],
    benefits: [
      'Keeps the company in good standing with the MCA, avoiding "defaulting company" status',
      'Protects directors from disqualification risk tied to persistent non-filing',
      'Maintains a clean compliance record needed for loans, tenders and due diligence',
    ],
    process: [
      { title: 'Financial statements finalized', desc: 'Your accounts for the year are finalized and approved by the board.' },
      { title: 'AOC-4 filed', desc: 'Financial statements are filed with the ROC within 30 days of the AGM.' },
      { title: 'MGT-7 filed', desc: 'The annual return covering shareholding and company details is filed within 60 days of the AGM.' },
      { title: 'Acknowledgement shared', desc: 'You receive the filing receipts (SRNs) for your company records.' },
    ],
    timeline: 'AOC-4 is due within 30 days and MGT-7 within 60 days of the AGM — typically by late October/November for companies with a 31 March year-end.',
    mistakes: [
      { title: 'Assuming a dormant company is exempt', desc: 'Zero turnover or no business activity does not exempt a company from annual filing — the obligation continues from incorporation until formal closure.' },
      { title: 'Filing AOC-4 before the audit is finalized', desc: 'Financial statements filed before the statutory audit is complete often need to be revised and refiled, adding extra fees and delay.' },
    ],
    penalties: [
      { title: 'Late filing additional fee', desc: 'An additional government fee accrues per day of delay, which increases significantly the longer the filing is pending.' },
      { title: 'Director disqualification risk', desc: 'Persistent non-filing across multiple years can lead to disqualification of directors under the Companies Act.' },
    ],
    faqs: [
      { q: 'Do I need to file if my company had no business this year?', a: 'Yes — annual ROC filings are mandatory for every registered company regardless of turnover or activity level.' },
      { q: 'What is the difference between AOC-4 and MGT-7?', a: 'AOC-4 files your financial statements (balance sheet, P&L); MGT-7 is the annual return covering shareholding pattern, directors and other company details.' },
      { q: 'Is a statutory audit required before filing?', a: 'Yes, your financial statements need to be audited by a Chartered Accountant before they can be filed via AOC-4.' },
      { q: 'What happens if I skip annual filing for multiple years?', a: "The ROC can mark your company as a defaulter, strike it off the register, and directors can face disqualification from holding directorships elsewhere." },
    ],
  },

  DIR3_KYC: {
    intro: [
      'Every individual holding a Director Identification Number (DIN) must complete DIR-3 KYC annually to keep their DIN active — even if they are not currently serving as a director in any company.',
      'SolveTax completes your annual DIR-3 KYC filing before the deadline, so your DIN stays active and you avoid the deactivation penalty.',
    ],
    eligibilityTitle: 'Who needs to file DIR-3 KYC?',
    eligibility: [
      'Every individual allotted a DIN on or before the end of a financial year',
      'Applies whether or not the person is currently an active director',
      'Required every year, even if no details have changed since the last filing',
    ],
    documents: [
      'DIN and PAN',
      'Aadhaar (linked to a working mobile number for OTP)',
      'Current personal email ID and mobile number',
      'Proof of permanent address, if it has changed since the last filing',
    ],
    benefits: [
      'Keeps your DIN active, avoiding the ₹5,000 reactivation fee',
      'Ensures you remain eligible to be appointed or continue as a director without interruption',
    ],
    process: [
      { title: 'Share your DIN & details', desc: 'Provide your DIN, PAN, Aadhaar and current contact details.' },
      { title: 'OTP verification', desc: 'Your mobile number and email are verified via OTP as part of the filing.' },
      { title: 'Form filed', desc: 'DIR-3 KYC (or the simpler web-based KYC if no details changed) is filed with the MCA.' },
    ],
    timeline: 'Due by 30 September every year for DINs allotted up to the end of the preceding financial year.',
    mistakes: [
      { title: 'Assuming KYC isn\'t needed after resigning as director', desc: 'The obligation is tied to holding a DIN, not to an active directorship — many people miss this and get an unexpected deactivation.' },
      { title: 'Using an Aadhaar-linked mobile number that\'s no longer active', desc: 'OTP verification fails if the linked number is out of use, which then requires an Aadhaar update before KYC can be completed.' },
    ],
    penalties: [
      { title: 'DIN deactivation', desc: 'Your DIN is marked "Deactivated due to non-filing of DIR-3 KYC" if the deadline is missed.' },
      { title: 'Reactivation fee', desc: 'A late filing fee (currently ₹5,000) applies to reactivate a deactivated DIN.' },
    ],
    faqs: [
      { q: 'Do I need to file DIR-3 KYC if I resigned as a director?', a: 'Yes, as long as your DIN remains allotted to you, annual KYC is required regardless of whether you currently hold a directorship.' },
      { q: 'What is the difference between DIR-3 KYC and web-based KYC?', a: 'The full DIR-3 KYC form is needed the first time or when details change; a simpler web-based KYC (just OTP verification) suffices in subsequent years if nothing has changed.' },
      { q: 'What happens if my DIN gets deactivated?', a: 'You cannot be appointed or continue as a director on any company filing until you file DIR-3 KYC with the late fee and reactivate it.' },
    ],
  },

  DIRECTOR_CHANGE: {
    intro: [
      'Adding, removing or changing a director requires filing the appropriate forms with the ROC within the prescribed timeline — board and shareholder approvals need to be properly documented alongside the filing.',
      'SolveTax prepares the board resolutions, consent letters and DIR-12 filing needed for a smooth, compliant director change.',
    ],
    eligibilityTitle: 'When do you need this filing?',
    eligibility: [
      'Appointing a new director to the board',
      'A director resigning or being removed from the board',
      'Change in designation of an existing director (e.g., to Managing Director)',
    ],
    documents: [
      'PAN, Aadhaar and address proof of the new/outgoing director',
      'Passport-size photograph and signed consent to act as director (Form DIR-2)',
      'Board resolution approving the appointment/resignation/change',
      'Resignation letter, in case of a resigning director',
    ],
    benefits: [
      'Keeps the company\'s MCA master data accurate and legally consistent',
      'Avoids disputes later over who was authorized to act on the company\'s behalf during a given period',
    ],
    process: [
      { title: 'Board resolution passed', desc: 'The board approves the appointment, resignation or change in designation.' },
      { title: 'Consent & DIN obtained', desc: 'For a new director, consent to act (DIR-2) and DIN allotment are completed if not already held.' },
      { title: 'DIR-12 filed', desc: 'The change is filed with the ROC within 30 days of the resolution.' },
      { title: 'Updated MCA records', desc: 'The company master data on the MCA portal reflects the updated director details.' },
    ],
    timeline: 'DIR-12 must be filed within 30 days of the board resolution or resignation date to avoid additional fees.',
    mistakes: [
      { title: 'Delaying the DIR-12 filing past 30 days', desc: 'An additional government fee kicks in per day of delay, and the MCA master data continues showing the outgoing director as active in the meantime.' },
      { title: 'Not filing DIR-11 as the resigning director', desc: 'A resigning director should independently file DIR-11 — relying solely on the company to file DIR-12 can leave your own resignation undocumented if the company delays.' },
    ],
    faqs: [
      { q: 'Does a resigning director need to file anything themselves?', a: 'Yes, a resigning director can independently file Form DIR-11 to formally notify the ROC of their resignation, in addition to the company\'s DIR-12 filing.' },
      { q: 'Do I need a new DIN for every new director?', a: 'Only if the person does not already hold a DIN from a previous directorship — otherwise their existing DIN is used.' },
      { q: 'What documents are needed for a new director appointment?', a: "PAN, Aadhaar, address proof, a passport-size photo, and a signed consent to act as director (Form DIR-2)." },
    ],
  },

  // ===========================================================================
  // ACCOUNTING
  // ===========================================================================
  MONTHLY_ACCOUNTING: {
    intro: [
      "Consistent monthly bookkeeping keeps your GST filings, TDS compliance and year-end closing accurate and stress-free. Trying to reconstruct a year of transactions at tax time is where most errors — and missed deductions — happen.",
      'SolveTax maintains your books in Tally or Zoho Books every month, reconciles bank transactions and gives you a simple MIS report so you always know where your business stands.',
    ],
    eligibilityTitle: 'Who needs monthly accounting support?',
    eligibility: [
      'Businesses without an in-house accountant or finance team',
      'GST-registered businesses needing accurate books for monthly return filing',
      'Growing businesses that need timely MIS reports for decision-making',
    ],
    documents: [
      'Sales and purchase invoices for the month',
      'Bank and credit card statements',
      'Expense receipts and vendor bills',
      'Previous month\'s books/opening balances, if this is your first month with us',
    ],
    benefits: [
      'Accurate, always-current books instead of a year-end scramble',
      'Bank reconciliation done monthly, catching errors early',
      'GST and TDS data ready in time for filing deadlines',
      'Monthly MIS reports giving visibility into revenue, expenses and cash flow',
    ],
    process: [
      { title: 'Share your transactions', desc: 'Send invoices, bank statements and expense receipts each month, or connect your accounting software.' },
      { title: 'Books updated', desc: 'Your accountant records and categorizes every transaction in Tally/Zoho Books.' },
      { title: 'Bank reconciliation', desc: 'Bank statements are reconciled against your books to catch discrepancies early.' },
      { title: 'Monthly MIS shared', desc: 'You receive a simple report showing revenue, expenses and key numbers for the month.' },
    ],
    timeline: 'Books are typically updated and reconciled within 5–7 working days after month-end, in time for GST and other filing deadlines.',
    mistakes: [
      { title: 'Mixing personal and business expenses', desc: 'Running personal transactions through the business bank account makes reconciliation harder and can distort your actual profitability.' },
      { title: 'Waiting until year-end to start bookkeeping', desc: 'Reconstructing a full year of transactions at once is where most missed deductions and reconciliation errors happen — monthly upkeep avoids this entirely.' },
    ],
    faqs: [
      { q: 'Do I need to use a specific accounting software?', a: 'We commonly work with Tally and Zoho Books, but can adapt to your existing setup or recommend one if you are starting fresh.' },
      { q: 'Can this feed directly into my GST filing?', a: 'Yes — keeping books current every month makes your GST return preparation faster and more accurate.' },
      { q: 'What if my books are already behind?', a: "We can catch up backlogged bookkeeping before moving to a regular monthly cycle — just let us know how far behind you are." },
      { q: 'Will I get visibility into my numbers, or just compliance-ready books?', a: 'Both — alongside compliance-ready books, you get a simple monthly MIS report so you can actually use the numbers for decisions, not just filing.' },
    ],
  },

  YEAR_END_FINALIZATION: {
    intro: [
      'Year-end finalization closes your books for the financial year — finalizing ledgers, computing depreciation, reconciling GST and TDS, and preparing financial statements ready for audit and income tax filing.',
      'SolveTax finalizes your accounts accurately and on time, so your ITR filing, ROC filing (if applicable) and any statutory audit proceed without last-minute issues.',
    ],
    eligibilityTitle: 'Who needs year-end finalization?',
    eligibility: [
      'Businesses and companies closing their books for the financial year',
      'Companies requiring audited financial statements for ROC filing',
      'Businesses wanting accurate financials before ITR filing',
    ],
    documents: [
      'Full year\'s books of account (sales, purchase and expense records)',
      'Bank statements for the entire financial year',
      'Fixed asset register and loan/investment statements',
      'All GST returns filed during the year, for reconciliation',
    ],
    benefits: [
      'A clean, audit-ready set of financials instead of a rushed year-end scramble',
      'Discrepancies between books and GST/TDS returns caught and resolved before filing',
      'A solid, accurate base for ITR and ROC filing deadlines',
    ],
    process: [
      { title: 'Books reviewed', desc: "A full year's transactions are reviewed for completeness and accuracy." },
      { title: 'Reconciliations completed', desc: 'GST, TDS and bank reconciliations are finalized against your annual returns.' },
      { title: 'Depreciation & adjustments', desc: 'Depreciation, provisions and year-end adjustment entries are recorded.' },
      { title: 'Financial statements prepared', desc: 'Balance sheet, P&L and notes are finalized, ready for audit and filing.' },
    ],
    timeline: 'Best started within a few weeks of financial year-end, to leave adequate time for statutory audit (if applicable) and the ITR/ROC filing deadlines that follow.',
    mistakes: [
      { title: 'Starting too close to the filing deadline', desc: 'Leaving finalization until just before ITR/ROC due dates leaves no room to resolve reconciliation gaps properly.' },
      { title: 'Missing depreciation on the correct block of assets', desc: 'Incorrect asset classification or missed additions/disposals during the year understate or overstate your actual tax position.' },
    ],
    faqs: [
      { q: 'Is a statutory audit included in this service?', a: 'We prepare finalized financials ready for audit; the statutory audit itself is conducted by an independent Chartered Accountant as required by law for companies.' },
      { q: 'When should I start year-end finalization?', a: "As soon as possible after your financial year ends, to leave enough time for audit (if applicable) and ITR/ROC filing deadlines." },
      { q: 'What if there are discrepancies between GST returns and books?', a: 'We identify and reconcile these differences during finalization, which is far easier to resolve at year-end than after filing your annual return.' },
      { q: 'Do I need this if I already do monthly bookkeeping with SolveTax?', a: 'It builds directly on it — monthly bookkeeping keeps things current through the year, and year-end finalization closes the books formally with the additional adjustments audit and filing require.' },
    ],
  },

  // ===========================================================================
  // PAYROLL
  // ===========================================================================
  PAYROLL_PROCESSING: {
    intro: [
      'Payroll processing covers salary computation, payslip generation, TDS on salaries and statutory compliance (PF, ESI, professional tax) every month — getting any of these wrong creates compliance risk and unhappy employees.',
      'SolveTax runs your monthly payroll accurately, handles statutory deductions and gives your team clean, timely payslips.',
    ],
    eligibilityTitle: 'Who needs payroll processing support?',
    eligibility: [
      'Businesses with employees who need accurate, on-time salary processing',
      'Companies required to comply with PF, ESI and professional tax regulations',
      'Growing teams that have outgrown manual salary calculation',
    ],
    documents: [
      'Employee master data (salary structure, PAN, bank details)',
      'Monthly attendance and leave records',
      'Details of new hires, exits and salary revisions for the month',
      'Investment declarations, for accurate TDS computation',
    ],
    benefits: [
      'Accurate salary computation with correct TDS deduction under the new/old regime',
      'Statutory compliance (PF, ESI, PT) handled alongside payroll',
      'Timely, professional payslips for every employee, every month',
      'Reduces compliance risk from manual calculation errors',
    ],
    process: [
      { title: 'Share employee & attendance data', desc: 'Send salary structures, attendance and any changes (new hires, exits, increments) each month.' },
      { title: 'Payroll computed', desc: 'Gross-to-net salary is computed including TDS, PF, ESI and professional tax deductions.' },
      { title: 'Review & approval', desc: 'You review the payroll summary before disbursal.' },
      { title: 'Payslips & compliance filed', desc: 'Payslips are generated and statutory dues (PF/ESI) are filed for the month.' },
    ],
    timeline: 'Payroll is typically processed and payslips shared 2–3 working days before your regular salary disbursal date each month.',
    mistakes: [
      { title: 'Not updating investment declarations mid-year', desc: 'TDS ends up over- or under-deducted through the year if an employee\'s declared investments aren\'t revised when their situation changes.' },
      { title: 'Missing full-and-final settlement compliance for exits', desc: 'Exit payroll has its own set of dues (leave encashment, gratuity where applicable) that are easy to miss if not handled as a distinct step.' },
    ],
    faqs: [
      { q: 'Does this include PF and ESI filing?', a: 'Payroll computation includes these deductions; SolveTax also offers dedicated PF Filing and ESI Filing services to complete the statutory filings each month.' },
      { q: 'Can you handle both old and new tax regime employees?', a: 'Yes, TDS is computed individually per employee based on their chosen tax regime and declared investments.' },
      { q: 'What if my team size changes frequently?', a: "We handle new hires, exits and salary revisions as part of the monthly cycle — just share the changes before each payroll run." },
      { q: 'Can employees access their own payslips and Form 16?', a: 'Yes, individual payslips are shared each month, and Form 16 is generated at year-end for every employee.' },
    ],
  },

  PF_FILING: {
    intro: [
      'Employers registered with the EPFO must file monthly PF returns and deposit employee and employer contributions by the 15th of the following month. Missing this attracts damages and interest under the EPF Act.',
      'SolveTax computes your monthly PF liability, generates the ECR (Electronic Challan cum Return) and ensures it is filed and paid on time.',
    ],
    eligibilityTitle: 'Who needs PF filing?',
    eligibility: [
      'Establishments with 20 or more employees (mandatory registration)',
      'Smaller establishments that have voluntarily registered with the EPFO',
      'Applies to eligible employees earning up to the statutory wage ceiling, and voluntarily for higher earners',
    ],
    documents: [
      'PF establishment code (UAN portal login)',
      'Monthly wage register for all PF-eligible employees',
      'Details of new joiners/exits during the month with their UANs',
    ],
    benefits: [
      'Avoids damages and interest that compound the longer a delay continues',
      'Keeps employees\' PF passbooks accurately updated every month',
      'Maintains a clean compliance record checked during any EPFO inspection',
    ],
    process: [
      { title: 'Share monthly wage data', desc: 'Send salary details for all PF-eligible employees for the month.' },
      { title: 'ECR prepared', desc: 'The Electronic Challan cum Return is prepared with employee and employer contributions.' },
      { title: 'Filed & paid', desc: 'The ECR is filed and payment is made through the EPFO portal before the 15th.' },
      { title: 'Challan shared', desc: 'You receive the payment confirmation and filing receipt for your records.' },
    ],
    timeline: 'PF contribution and return filing is due by the 15th of the following month.',
    penalties: [
      { title: 'Damages for late payment', desc: 'Ranges from 5% to 25% per annum depending on the period of delay, under the EPF Act.' },
      { title: 'Interest on delayed payment', desc: '12% per annum interest also applies in addition to damages.' },
    ],
    mistakes: [
      { title: 'Excluding eligible employees from PF wages', desc: 'Structuring salary to keep basic wages artificially low to avoid PF contribution is a common practice that EPFO inspections specifically look for.' },
      { title: 'Missing new joiners in the first month\'s ECR', desc: 'Forgetting to add a new employee\'s UAN in the month they join means their contribution is filed late even if the overall ECR is on time.' },
    ],
    faqs: [
      { q: 'Is PF registration mandatory for my business?', a: 'It is mandatory once you have 20 or more employees; smaller establishments can register voluntarily.' },
      { q: 'What is the current PF contribution rate?', a: 'Typically 12% of basic wages from both employer and employee, subject to the statutory wage ceiling and applicable scheme rules.' },
      { q: 'What happens if we miss a month\'s PF filing?', a: 'Damages and interest accrue on the delayed amount, and repeated defaults can invite inspection from the EPFO.' },
      { q: 'Can employees track their own PF balance?', a: 'Yes, employees can check their PF balance and passbook directly on the EPFO portal or UMANG app using their UAN.' },
    ],
  },

  ESI_FILING: {
    intro: [
      'Employers covered under the ESI Act must file monthly contributions for eligible employees, providing them medical and cash benefits. Filing is due by the 15th of the following month, alongside PF.',
      'SolveTax computes your ESI liability each month and files your contribution on time, keeping your establishment compliant.',
    ],
    eligibilityTitle: 'Who needs ESI filing?',
    eligibility: [
      'Establishments with 10 or more employees in most states (threshold varies by state)',
      'Employees earning up to the statutory wage ceiling (currently ₹21,000/month) are covered',
      'Mandatory once the establishment and employee both meet eligibility criteria',
    ],
    documents: [
      'ESIC establishment code',
      'Monthly wage register for ESI-eligible employees',
      'Details of new joiners/exits with their Insurance Number (IP number)',
    ],
    benefits: [
      'Employees get access to medical, sickness and maternity benefits without a separate claim process',
      'Avoids compounding interest and damages from delayed contribution',
      'Keeps your establishment compliant for ESIC inspections',
    ],
    process: [
      { title: 'Share monthly wage data', desc: 'Send wage details for all ESI-eligible employees.' },
      { title: 'Contribution computed', desc: 'Employee (0.75%) and employer (3.25%) contributions are calculated on gross wages.' },
      { title: 'Filed & paid', desc: 'The monthly contribution is filed and paid through the ESIC portal before the 15th.' },
    ],
    timeline: 'ESI contribution and return filing is due by the 15th of the following month.',
    mistakes: [
      { title: 'Not re-checking eligibility as salaries increase', desc: 'An employee crossing the ₹21,000 wage ceiling mid-year needs their ESI status reviewed — continuing or stopping contributions incorrectly both cause compliance issues.' },
      { title: 'Delayed IP number generation for new joiners', desc: 'A new employee\'s Insurance Number needs to be generated promptly so their first month\'s contribution is filed correctly against it.' },
    ],
    penalties: [
      { title: 'Interest on delayed payment', desc: '12% per annum on the delayed contribution amount.' },
      { title: 'Damages under the ESI Act', desc: 'Additional damages can be levied by ESIC depending on the period of default.' },
    ],
    faqs: [
      { q: 'What is the current wage ceiling for ESI eligibility?', a: 'Employees earning up to ₹21,000 per month (₹25,000 for persons with disabilities) are covered under the ESI scheme.' },
      { q: 'What benefits do employees get under ESI?', a: 'Medical treatment for the employee and dependents, sickness benefit, maternity benefit and disability benefits, among others.' },
      { q: 'Do all businesses need ESI registration?', a: 'It becomes mandatory once the employee count threshold (typically 10, varies by state) is met and eligible wages are being paid.' },
      { q: 'What happens if an employee\'s salary crosses the wage ceiling?', a: 'They continue to be covered for the rest of that contribution period (typically till the period ends) even after crossing the ceiling, as per ESI scheme rules.' },
    ],
  },

  // ===========================================================================
  // TRADEMARK
  // ===========================================================================
  TRADEMARK_REG: {
    intro: [
      'A trademark protects your brand name, logo or tagline from being used by competitors — giving you exclusive legal rights to it across India once registered. Without registration, your brand identity has far weaker legal protection, even if you have used it for years.',
      'SolveTax runs a trademark search to check availability, files your application in the right class, and represents you through the entire registration process to certificate.',
    ],
    eligibilityTitle: 'Who should register a trademark?',
    eligibility: [
      'Any business, startup or individual with a brand name, logo or tagline to protect',
      'E-commerce sellers wanting to protect their brand on marketplaces',
      'Businesses expanding who want to prevent others from using a similar name',
      'Anyone who wants to license or eventually sell their brand as an asset',
    ],
    benefits: [
      'Exclusive nationwide right to use your mark for the goods/services registered',
      'Legal recourse (infringement action) against copycats and lookalikes',
      'The ® symbol adds credibility and deters imitation',
      'A registered trademark is a transferable, licensable business asset',
      'Protects your brand as you expand into e-commerce and new markets',
    ],
    documents: [
      'Brand name / logo to be registered',
      'Applicant PAN and identity proof (individual/company/LLP)',
      'Udyam registration certificate, if claiming MSME fee discount',
      'Power of attorney (Form TM-48) authorizing filing on your behalf',
    ],
    process: [
      { title: 'Trademark search', desc: 'We check the registry for identical or deceptively similar existing marks in your class.' },
      { title: 'Application filed', desc: 'Filed under the correct class(es) of goods/services with the Trademark Registry.' },
      { title: 'Examination', desc: 'The Registrar examines the application and may raise objections, which we respond to if needed.' },
      { title: 'Publication in journal', desc: 'The mark is published in the Trademark Journal for 4 months, open to third-party opposition.' },
      { title: 'Registration certificate', desc: 'If unopposed (or opposition is resolved in your favor), the certificate is issued.' },
    ],
    timeline: 'The full process typically takes 12–18 months given statutory examination and publication timelines, though the application itself is filed within days.',
    mistakes: [
      { title: 'Skipping the trademark search before filing', desc: 'Filing without checking for identical or deceptively similar existing marks often leads to an objection or opposition that could have been avoided upfront.' },
      { title: 'Registering under the wrong class', desc: 'Choosing a class that doesn\'t match your actual goods/services means your protection doesn\'t actually cover what you sell.' },
      { title: 'Not responding to the examination report on time', desc: 'A missed 30-day deadline gets the application treated as abandoned, requiring a fresh filing from scratch.' },
    ],
    faqs: [
      { q: 'How long does trademark registration take?', a: 'Filing takes just a few days, but the full process to a granted certificate typically takes 12–18 months due to mandatory examination and publication periods.' },
      { q: 'Can I use ™ before my trademark is registered?', a: 'Yes, you can use ™ as soon as you file the application; the ® symbol should only be used once registration is granted.' },
      { q: 'What happens if someone objects to my trademark?', a: "You get an opportunity to respond during examination, and if published, opposition follows a hearing process — SolveTax represents you at each stage." },
      { q: 'Do I need to register in multiple classes?', a: 'Only if you operate across different categories of goods/services — we advise on the right classes to cover your business comprehensively.' },
      { q: 'Is a trademark valid across all of India?', a: 'Yes, a registered trademark gives you exclusive rights across the entire country, not just your state.' },
      { q: 'How long is a trademark valid?', a: '10 years from the date of filing, renewable indefinitely in further 10-year terms.' },
    ],
  },

  TRADEMARK_RENEWAL: {
    intro: [
      'A registered trademark is valid for 10 years and must be renewed before expiry to keep your protection active. Missing the renewal window can mean losing your rights, or paying a steep surcharge to restore a lapsed mark.',
      'SolveTax tracks your renewal due date and files Form TM-R well in time, so your trademark protection never lapses.',
    ],
    eligibilityTitle: 'When should you renew?',
    eligibility: [
      'Trademark owners approaching their 10-year registration anniversary',
      'Marks that have already lapsed and need restoration within the permitted window',
    ],
    documents: [
      'Registration certificate / trademark registration number',
      'Current owner details (in case of any change since registration)',
      'Power of attorney (Form TM-48), if filed through an agent',
    ],
    benefits: [
      'Uninterrupted legal protection and exclusive rights to your mark',
      'Avoids the surcharge and risk that come with restoring a lapsed mark',
      'Keeps your ® status and licensing/assignment value intact',
    ],
    process: [
      { title: 'Renewal reminder', desc: 'We track your registration date and notify you well before the renewal window opens.' },
      { title: 'Form TM-R filed', desc: 'The renewal application is filed with the Trademark Registry, along with the fee.' },
      { title: 'Confirmation', desc: 'You receive confirmation once the renewal is recorded against your registration.' },
    ],
    timeline: 'Renewal can be filed up to 1 year before expiry, and up to 6 months after expiry with a surcharge (restoration).',
    mistakes: [
      { title: 'Not updating your address before renewal reminders are due', desc: 'If your registered address is outdated, you may miss the official renewal reminder from the Registry entirely.' },
    ],
    penalties: [
      { title: 'Lapsed trademark', desc: 'If not renewed within the restoration window, the mark is removed from the register and becomes available for others to register.' },
    ],
    faqs: [
      { q: 'What happens if I miss my renewal date?', a: 'You have a grace period (typically up to 6 months) to restore the mark with a surcharge before it lapses permanently.' },
      { q: 'How much does renewal cost?', a: 'Renewal fees depend on whether you file directly or through the restoration process — we quote based on your specific timeline.' },
      { q: 'Can I make changes to my trademark at renewal?', a: 'Renewal keeps the mark as originally registered; any changes to the mark itself would require a fresh application.' },
    ],
  },

  TM_INFRINGEMENT_NOTICE: {
    intro: [
      "When someone uses a mark identical or deceptively similar to your registered trademark, a cease-and-desist notice is usually the fastest, most cost-effective first step — before considering court action.",
      'SolveTax drafts and sends a legally sound infringement notice on your behalf, demanding the infringing party stop use and, where appropriate, compensate you.',
    ],
    eligibilityTitle: 'When should you send an infringement notice?',
    eligibility: [
      'You hold a registered trademark and found another business using an identical/similar mark',
      'Unauthorized use is causing customer confusion or diverting business',
      'You want to establish a documented record before pursuing further legal action if needed',
    ],
    documents: [
      'Your trademark registration certificate',
      'Evidence of the infringing use (screenshots, product samples, listings)',
      'Details of the infringing party, where known',
    ],
    benefits: [
      'Often resolves the matter quickly without needing to go to court',
      'Creates a documented paper trail that strengthens any future legal action',
      'Signals that your brand is actively enforced, deterring further copying',
    ],
    process: [
      { title: 'Evidence gathered', desc: 'We document the infringing use — screenshots, product samples, listings, etc.' },
      { title: 'Notice drafted', desc: 'A legal notice is drafted citing your registration and demanding cessation of use.' },
      { title: 'Notice sent', desc: 'Delivered to the infringing party via registered post/courier with a response deadline.' },
      { title: 'Follow-up', desc: 'We advise on next steps based on their response (or non-response).' },
    ],
    timeline: 'A notice is typically drafted and sent within 2–3 working days, with a response deadline of 15–30 days given to the recipient.',
    faqs: [
      { q: 'Do I need a registered trademark to send an infringement notice?', a: 'A registered trademark gives you the strongest legal standing, though passing-off action is available even for well-established unregistered marks in some cases.' },
      { q: 'What if the other party ignores the notice?', a: 'You can escalate to filing a suit for infringement and seeking an injunction and damages through the appropriate court.' },
      { q: 'How quickly should I act after discovering infringement?', a: "As soon as possible — delay can weaken your position and allow the infringing use to become more established." },
    ],
  },

  TRADEMARK_OBJECTION: {
    intro: [
      "An examination report objection is raised by the Trademark Registrar during review — commonly citing similarity to an existing mark, or that the mark is too generic/descriptive to register. A well-argued response is critical to keep your application alive.",
      "SolveTax reviews the objection, builds the strongest possible response with evidence of distinctiveness or prior use, and files it within the deadline.",
    ],
    eligibilityTitle: 'Common grounds for objection',
    eligibility: [
      'Similarity to an existing registered or pending trademark',
      'The mark is considered descriptive or generic for the goods/services',
      'Incomplete or incorrect information in the original application',
    ],
    documents: [
      'The examination report received from the Registry',
      'Evidence of prior/continuous use of the mark (invoices, packaging, ads), if applicable',
      'Any distinctiveness evidence (sales figures, marketing spend, media coverage)',
    ],
    benefits: [
      'Keeps your application alive instead of risking abandonment',
      'A well-argued response can overcome most objections without needing a hearing',
    ],
    process: [
      { title: 'Objection reviewed', desc: 'We analyze the examination report to understand the exact ground of objection.' },
      { title: 'Response drafted', desc: 'A reply is prepared with legal arguments and, where relevant, evidence of prior use or distinctiveness.' },
      { title: 'Filed within deadline', desc: 'The response is submitted within 30 days of the examination report.' },
      { title: 'Hearing (if required)', desc: 'If the Registrar isn\'t satisfied on paper, we represent you at the show-cause hearing.' },
    ],
    timeline: 'The response to an examination report must be filed within 30 days of receipt.',
    faqs: [
      { q: 'What happens if I miss the 30-day deadline?', a: 'Your application is treated as abandoned, and you would need to file a fresh application if you still want to register the mark.' },
      { q: 'Can an objection be overcome?', a: 'Yes, many objections are successfully overcome with a well-reasoned response and, where applicable, evidence of extensive prior use and distinctiveness.' },
      { q: 'Is a hearing always required?', a: 'No — if your written response satisfies the examiner, the application proceeds to publication without a hearing.' },
    ],
  },

  TRADEMARK_CERTIFICATE: {
    intro: [
      'Once your trademark clears examination and publication without opposition (or opposition is resolved in your favor), the Registry issues your official Registration Certificate — the document confirming your legal ownership of the mark.',
      "SolveTax tracks your application status and obtains your registration certificate as soon as it's issued, keeping a certified copy on record for you.",
    ],
    eligibilityTitle: 'When can you obtain your certificate?',
    eligibility: [
      'Your application has been published in the Trademark Journal with no opposition filed within 4 months',
      'Any opposition filed against your mark has been resolved in your favor',
    ],
    benefits: [
      'Official, government-issued proof of your exclusive rights to the mark',
      'Enables you to start using the ® symbol correctly',
      'Required documentation for licensing, assignment or enforcement action',
    ],
    process: [
      { title: 'Status tracked', desc: 'We monitor your application through the publication and opposition window.' },
      { title: 'Certificate issued', desc: 'Once the statutory period passes without opposition, the Registry issues the certificate.' },
      { title: 'Delivered to you', desc: 'We share the digital certificate and guide you on using the ® symbol correctly.' },
    ],
    faqs: [
      { q: 'How do I know when my certificate is ready?', a: 'We track your application status on the official Trademark Registry portal and notify you as soon as it is issued.' },
      { q: 'Can I download my own certificate?', a: 'Yes, once issued it is available on the IP India website against your application number — we can also provide a certified copy.' },
      { q: 'What if my certificate has an error?', a: 'Errors in the issued certificate can be corrected through a rectification request with the Registry.' },
    ],
  },

  TRADEMARK_OPPOSITION: {
    intro: [
      "Once a trademark is published in the journal, anyone can file an opposition within 4 months if they believe it conflicts with their rights. Whether you're opposing someone else's mark or defending your own published application, the process involves a defined pleadings and evidence stage.",
      'SolveTax prepares your opposition or counter-statement with the required evidence, and represents you through the proceeding.',
    ],
    eligibilityTitle: 'When does opposition apply?',
    eligibility: [
      'You believe a newly published trademark conflicts with your existing rights (filing opposition)',
      'Your own published trademark has been opposed by a third party (defending)',
    ],
    documents: [
      'Your existing trademark registration/application details, if filing opposition',
      'Evidence of prior use or reputation in the mark',
      'The opposition notice received, if you are defending your own mark',
    ],
    benefits: [
      'Protects your existing brand rights from a conflicting new registration',
      'Gives you a formal, documented process to resolve the conflict instead of an informal dispute',
    ],
    process: [
      { title: 'Notice of opposition / counter-statement', desc: 'Filed within the statutory deadline after publication or receipt of opposition.' },
      { title: 'Evidence stage', desc: 'Both parties file evidence in support of their position in rounds set by the Registry.' },
      { title: 'Hearing', desc: 'The Registrar hears both sides before deciding the outcome.' },
      { title: 'Decision', desc: 'The Registrar either allows the opposed mark to proceed to registration or refuses it.' },
    ],
    timeline: 'Opposition must be filed within 4 months of publication; the counter-statement is due within 2 months of receiving the opposition notice.',
    faqs: [
      { q: 'What happens if I don\'t respond to an opposition against my mark?', a: 'Your application is treated as abandoned if you fail to file a counter-statement within the 2-month deadline.' },
      { q: 'How long does an opposition proceeding take?', a: 'It can take anywhere from 1–3 years depending on evidence rounds and hearing schedules at the Registry.' },
      { q: 'Can opposition proceedings be settled?', a: 'Yes, parties can reach a settlement (such as a coexistence or amendment agreement) at any stage, ending the dispute without a full hearing.' },
    ],
  },

  TRADEMARK_HEARING: {
    intro: [
      'A show-cause hearing is scheduled when the Registrar is not satisfied with your written response to an objection, or as part of an opposition proceeding. Representation at the hearing is important — this is often the deciding stage for your application.',
      'SolveTax prepares your arguments and represents your case before the Registrar, whether virtually or in person.',
    ],
    eligibilityTitle: 'When is a hearing scheduled?',
    eligibility: [
      'Your written response to an examination objection wasn\'t enough to satisfy the Registrar',
      'You are a party to an opposition proceeding that has reached the hearing stage',
    ],
    documents: [
      'The hearing notice received from the Registry',
      'Your original application, examination report and all prior correspondence',
      'Any additional evidence supporting your case (usage proof, distinctiveness evidence)',
    ],
    benefits: [
      'Skilled representation at what is often the deciding stage of the application',
      'A well-prepared case can turn around an application that looked headed for refusal',
    ],
    process: [
      { title: 'Hearing notice reviewed', desc: 'We review the hearing notice and the full application history to prepare your case.' },
      { title: 'Arguments prepared', desc: 'Legal arguments and supporting evidence are organized ahead of the hearing.' },
      { title: 'Representation at hearing', desc: 'We appear on your behalf before the Registrar and present your case.' },
      { title: 'Outcome tracked', desc: "We track and inform you of the Registrar's decision following the hearing." },
    ],
    faqs: [
      { q: 'Do I need to attend the hearing myself?', a: 'No, an authorized agent or attorney can represent you at the hearing — SolveTax handles this on your behalf.' },
      { q: 'What happens if I miss my scheduled hearing?', a: 'Your application can be treated as abandoned, so it is important to respond and attend, or request an adjournment through proper channels if genuinely needed.' },
      { q: 'How is the hearing outcome communicated?', a: 'The Registrar typically issues a written order after the hearing, which we track and share with you.' },
    ],
  },

  TRADEMARK_RECTIFICATION: {
    intro: [
      'Trademark rectification corrects errors in the register — such as a wrongly recorded owner name or address — or can be used to cancel a registered mark that should not have been granted or is no longer in use.',
      'SolveTax files rectification applications for corrections to your own registration, or on your behalf to challenge a wrongly registered competing mark.',
    ],
    eligibilityTitle: 'When is rectification used?',
    eligibility: [
      'Correcting clerical errors in your own trademark registration (name, address, goods description)',
      'Cancelling a registered mark that conflicts with your earlier rights',
      'Removing a mark that has not been used for a continuous period (non-use cancellation)',
    ],
    documents: [
      'The registration certificate (yours, for correction; or the contested mark\'s, for cancellation)',
      'Evidence supporting the grounds for correction or cancellation',
      'Proof of your own prior rights, if seeking to cancel a conflicting mark',
    ],
    benefits: [
      'Keeps the official register accurate for your own registration',
      'Removes conflicting or unused marks that block your brand strategy',
    ],
    process: [
      { title: 'Grounds identified', desc: 'We determine whether the case is a correction to your own mark or a cancellation action against another.' },
      { title: 'Application filed', desc: 'Filed before the Registrar or the appropriate court/tribunal with supporting grounds.' },
      { title: 'Response & hearing', desc: 'The other party (if any) is heard before a decision is made.' },
      { title: 'Register updated', desc: 'The correction or cancellation is reflected on the official register once approved.' },
    ],
    faqs: [
      { q: 'Can I fix a typo in my own registered trademark?', a: 'Yes, minor clerical errors like a misspelled address can be corrected through a rectification application.' },
      { q: "Can I cancel someone else's trademark?", a: 'Yes, if you can show it conflicts with your prior rights, was wrongly registered, or has not been used for the statutory non-use period.' },
      { q: 'How long does rectification take?', a: 'Simple corrections can be quick; cancellation actions involving a contested hearing can take considerably longer.' },
    ],
  },

  COPYRIGHT_REG: {
    intro: [
      'Copyright registration provides official, dated proof of authorship for literary, artistic, musical or software works — strengthening your position in any future infringement dispute, even though copyright technically exists from the moment of creation.',
      'SolveTax prepares and files your copyright application with the required work samples and supporting statements.',
    ],
    eligibilityTitle: 'What can be registered?',
    eligibility: [
      'Literary works — books, articles, software code',
      'Artistic works — logos, illustrations, designs, photographs',
      'Musical and sound recordings',
      'Cinematograph films',
    ],
    benefits: [
      'Official, dated government record of your authorship',
      'Stronger evidence in any infringement dispute',
      'Enables licensing and assignment of the work as a documented asset',
      'Valid for the author\'s lifetime plus 60 years (for most literary/artistic works)',
    ],
    documents: [
      'Copy/extract of the work being registered (manuscript, source code, artwork, etc.)',
      'Applicant\'s ID proof and proof of authorship',
      'No-objection certificate from co-authors/publishers, if applicable',
      'Power of attorney, if filed through an agent',
    ],
    process: [
      { title: 'Work classified', desc: 'We determine the correct category (literary, artistic, software, etc.) for your work.' },
      { title: 'Application filed', desc: 'Filed with the Copyright Office along with samples/extracts of the work.' },
      { title: 'Examination', desc: 'The Registrar reviews the application; objections (if any) are addressed.' },
      { title: 'Registration certificate', desc: 'Issued once the statutory objection period passes without issue.' },
    ],
    timeline: 'Registration typically takes 6–12 months from filing, including the mandatory waiting period for objections.',
    mistakes: [
      { title: 'Filing without co-author/publisher consent', desc: 'Missing a required no-objection certificate from a co-author or previous publisher is a common cause of delay or objection.' },
      { title: 'Submitting an incomplete work sample', desc: 'Partial or unclear extracts of the work can lead to queries that slow down the registration timeline.' },
    ],
    faqs: [
      { q: 'Do I need to register copyright to be protected?', a: 'No, copyright exists automatically upon creation, but registration provides strong, dated evidence that is valuable in any dispute.' },
      { q: 'Can I copyright software code?', a: 'Yes, software is registrable as a literary work, typically by submitting source code extracts.' },
      { q: 'How long does copyright protection last?', a: "Generally the author's lifetime plus 60 years for most works, with different terms for certain categories like sound recordings and films." },
    ],
  },

  COPYRIGHT_OBJECTION: {
    intro: [
      'Objections during copyright registration typically arise when someone claims the work is similar to an existing registered copyright, or disputes authorship. A timely, well-documented response is needed to keep the application moving.',
      "SolveTax reviews the objection and prepares a response supported by originality evidence and creation records.",
    ],
    eligibilityTitle: 'When does this apply?',
    eligibility: [
      'A third party has filed an objection claiming similarity to their work',
      'The Copyright Office examiner has raised a query on your application',
    ],
    documents: [
      'The objection notice received',
      'Dated drafts, source files or other evidence of independent creation',
      'Correspondence establishing your creation timeline',
    ],
    benefits: [
      'A well-documented response can keep your registration on track without a hearing',
      'Establishes a clear, defensible record of independent creation for any future dispute',
    ],
    process: [
      { title: 'Objection reviewed', desc: 'We examine the specific grounds raised by the objector or examiner.' },
      { title: 'Response prepared', desc: 'Evidence of originality, independent creation or authorship is compiled.' },
      { title: 'Filed with the Registry', desc: 'Submitted within the given timeline for consideration.' },
      { title: 'Hearing (if needed)', desc: 'We represent you if the matter proceeds to a hearing before the Copyright Board/Registrar.' },
    ],
    faqs: [
      { q: 'What is the most common reason for copyright objection?', a: 'A third party claiming the work is similar to, or copied from, their existing registered or unregistered work.' },
      { q: 'How do I prove independent creation?', a: 'Dated drafts, source files, correspondence and other creation records help establish that the work was independently created.' },
      { q: 'What if the objection is not resolved?', a: 'The matter can proceed to a hearing where both sides present their case before a decision is made.' },
    ],
  },

  PATENT_REG: {
    intro: [
      'A patent protects a novel, non-obvious and industrially applicable invention — giving you exclusive rights to make, use and sell it for 20 years. Patent filing and prosecution is technically detailed, requiring careful drafting of claims and specifications.',
      'SolveTax works with you and patent specialists to draft your specification, file the application and prosecute it through examination to grant.',
    ],
    eligibilityTitle: 'What can be patented?',
    eligibility: [
      'A new product, process or technical solution that is novel and not obvious',
      'The invention must have industrial applicability',
      'Must not fall under excluded categories (e.g., abstract ideas, discoveries, certain software-only claims)',
    ],
    documents: [
      'Detailed technical description and drawings of the invention',
      'Inventor and applicant details (individual/company/startup)',
      'Proof of right to file (assignment deed, if the applicant is not the inventor)',
      'Startup/MSME certificate, if claiming a fee concession',
    ],
    benefits: [
      'Exclusive rights to make, use, sell and license the invention for 20 years',
      'A patent is a valuable, transferable asset for licensing or acquisition',
      'Startups and small entities are eligible for a significant fee concession',
    ],
    process: [
      { title: 'Patentability search', desc: 'A prior-art search checks whether your invention is genuinely novel.' },
      { title: 'Specification drafted', desc: 'A detailed technical specification with claims is drafted (provisional or complete).' },
      { title: 'Application filed', desc: 'Filed with the Indian Patent Office, published after 18 months.' },
      { title: 'Examination & prosecution', desc: 'We respond to examination reports and objections raised by the examiner.' },
      { title: 'Grant', desc: 'Once all objections are resolved, the patent is granted and published.' },
    ],
    timeline: 'Patent grant in India typically takes 2–5 years from filing, depending on the technology area and examination queue, though expedited examination is available in certain cases.',
    mistakes: [
      { title: 'Publicly disclosing the invention before filing', desc: 'Demoing, publishing or selling the invention before filing a patent application can destroy its novelty and make it unpatentable.' },
      { title: 'Drafting overly narrow or overly broad claims', desc: 'Claims that are too narrow are easy to design around; claims that are too broad invite objections — this is why careful, professional drafting matters.' },
    ],
    faqs: [
      { q: 'What is the difference between a provisional and complete patent application?', a: 'A provisional application secures an early filing date while you finalize the invention; the complete specification (with full claims) must follow within 12 months.' },
      { q: 'How long does a patent last?', a: '20 years from the filing date, subject to payment of periodic renewal fees.' },
      { q: 'Can software be patented in India?', a: 'Pure software/algorithms are generally excluded, but software tied to a novel technical hardware solution or process can be patentable — this needs careful claim drafting.' },
      { q: 'Should I keep my invention confidential before filing?', a: 'Yes — public disclosure before filing can affect novelty; file first or use a non-disclosure agreement if you must discuss it beforehand.' },
    ],
  },

  TRADEMARK_TRANSFER: {
    intro: [
      "Trademark assignment lets you legally transfer ownership of a registered (or pending) mark to another person or entity — whether as part of a business sale, restructuring, or licensing arrangement.",
      'SolveTax drafts the assignment deed and files the required Form TM-P with the Registry to record the change of ownership.',
    ],
    eligibilityTitle: 'When is a trademark transfer needed?',
    eligibility: [
      'Selling a business along with its brand/trademark',
      'Transferring a mark between group companies or after restructuring',
      'Assigning rights to a mark as part of a licensing or franchise arrangement',
    ],
    documents: [
      'Existing trademark registration certificate',
      'Identity/incorporation proof of both assignor and assignee',
      'Signed assignment deed (with or without goodwill)',
    ],
    benefits: [
      'Legally documents the change of ownership, protecting both parties',
      'Keeps the official register accurate, which matters for future enforcement or licensing',
    ],
    process: [
      { title: 'Assignment deed drafted', desc: 'A deed capturing the terms of transfer (with or without goodwill) is prepared.' },
      { title: 'Form TM-P filed', desc: 'The application to record the assignment is filed with the Trademark Registry.' },
      { title: 'Public notice (if required)', desc: 'In some cases, the Registrar may require public notice of the assignment.' },
      { title: 'Register updated', desc: 'The new owner is reflected as the registered proprietor once approved.' },
    ],
    timeline: 'Recording an assignment typically takes 3–6 months for the Registry to process and reflect on the official register.',
    faqs: [
      { q: 'What is the difference between assignment "with goodwill" and "without goodwill"?', a: 'With goodwill transfers the mark along with the business reputation attached to it; without goodwill separates the mark from the business, which has specific legal implications.' },
      { q: 'Can a pending trademark application be transferred?', a: 'Yes, an application (not yet registered) can also be assigned, following the same recordal process.' },
      { q: 'How long does recording an assignment take?', a: 'It typically takes a few months for the Registry to process and update the register once the application is filed.' },
    ],
  },

  EXPEDITED_TM_REG: {
    intro: [
      'Expedited examination lets your trademark application jump the queue, cutting the wait for examination from many months down to about a month, for an additional government fee — useful when you need faster certainty on your brand.',
      'SolveTax files your expedited request alongside your application (or for a pending one) to fast-track your path toward registration.',
    ],
    eligibilityTitle: 'When should you consider expedited examination?',
    eligibility: [
      'Launching a brand soon and need faster clarity on registrability',
      'Facing a potential conflict and want to establish your registration priority sooner',
      'Preparing for a funding round or acquisition where clean IP ownership matters',
    ],
    documents: [
      'Your filed trademark application details (or the application you want expedited)',
      'Justification/supporting reason for requesting expedited examination',
    ],
    benefits: [
      'Cuts examination wait time from months to about a month',
      'Gives faster certainty on registrability before a launch or funding milestone',
    ],
    process: [
      { title: 'Expedited request filed', desc: 'Form TM-M is filed with the additional government fee, requesting priority examination.' },
      { title: 'Faster examination', desc: 'The application is examined ahead of the regular queue, typically within a month.' },
      { title: 'Standard process continues', desc: 'From here, publication and any opposition follow the normal timeline.' },
    ],
    timeline: 'Expedited examination typically happens within about 1 month of the request, versus several months to a year in the regular queue.',
    faqs: [
      { q: 'Does expedited examination guarantee faster registration overall?', a: 'It speeds up the examination stage specifically; the publication and opposition window afterward follows the normal statutory timeline.' },
      { q: 'How much extra does expedited examination cost?', a: 'A higher government fee applies compared to normal examination — we quote the exact current fee when you file.' },
      { q: 'Can I request expedition for an already-filed application?', a: 'Yes, as long as examination has not already started, you can request expedited processing for a pending application.' },
    ],
  },

  LOGO_DESIGNING: {
    intro: [
      'A strong, distinctive logo is both a branding asset and something that registers more easily as a trademark — vague or overly generic designs are harder to protect legally.',
      'SolveTax designs a professional logo for your brand with trademark registrability in mind, ready to use across your website, packaging and filing.',
    ],
    eligibilityTitle: 'Who should get a logo designed?',
    eligibility: [
      'New businesses and startups launching their brand identity',
      'Businesses rebranding or refreshing an outdated logo',
      'Anyone planning to trademark-register their brand mark soon after',
    ],
    documents: [
      'Business name and a short brief on your products/services',
      'Preferred colors, style references or competitor examples (optional)',
      'Target audience and where the logo will be used (web, packaging, signage)',
    ],
    benefits: [
      'A professional, distinctive visual identity for your brand',
      'Designed with trademark registrability in mind from the start',
      'Delivered in formats ready for web, print and filing purposes',
    ],
    process: [
      { title: 'Brand brief', desc: 'We understand your business, target audience and preferred style direction.' },
      { title: 'Concepts designed', desc: 'Initial logo concepts are shared for your feedback.' },
      { title: 'Refinement', desc: 'Your chosen direction is refined based on your input.' },
      { title: 'Final files delivered', desc: 'Final logo files are delivered in all formats you need (PNG, SVG, PDF, etc.).' },
    ],
    faqs: [
      { q: 'Can you also file for trademark registration once the logo is ready?', a: 'Yes — SolveTax offers Trademark Registration as a separate service, and we can proceed directly once your logo is finalized.' },
      { q: 'How many revisions are included?', a: 'We work with you through a reasonable number of revisions until you are happy with the final direction — details are confirmed when you get started.' },
      { q: 'Will I own full rights to the final logo?', a: 'Yes, full ownership and copyright in the final delivered design transfers to you.' },
    ],
  },

  DESIGN_REGISTRATION: {
    intro: [
      'Design registration protects the unique visual appearance of a product — its shape, pattern, configuration or ornamentation — under the Designs Act, distinct from trademark or patent protection.',
      'SolveTax files your design application with the Design Office, covering the visual features that make your product distinctive.',
    ],
    eligibilityTitle: 'What can be registered as a design?',
    eligibility: [
      'The shape, configuration, pattern or ornamentation applied to a product',
      'The design must be new/original and not previously published or used',
      'Purely functional features (with no aesthetic element) are not eligible',
    ],
    documents: [
      'Representations/drawings of the design from multiple views',
      'Statement of novelty describing the distinctive features',
      'Applicant details and proof of right to the design',
    ],
    benefits: [
      'Exclusive right to the registered design for up to 15 years',
      'Prevents competitors from copying your product\'s distinctive appearance',
      'Relatively quick registration process compared to patents',
    ],
    process: [
      { title: 'Novelty check', desc: 'We check that the design has not been previously published or registered.' },
      { title: 'Application filed', desc: 'Filed with the Design Office along with representations of the design.' },
      { title: 'Examination', desc: 'The Design Office examines the application for compliance and novelty.' },
      { title: 'Registration certificate', desc: 'Issued once the application clears examination.' },
    ],
    timeline: 'Design registration typically takes 6–12 months, faster than trademark or patent registration.',
    mistakes: [
      { title: 'Publicly launching the product before filing', desc: 'Selling or publicly showcasing the design before filing can destroy its novelty, the same way it does for patents.' },
      { title: 'Submitting unclear or incomplete representations', desc: 'Vague drawings that don\'t clearly show the distinctive features are a common cause of examination objections.' },
    ],
    faqs: [
      { q: 'How is design registration different from a trademark?', a: 'A trademark protects brand identifiers like names and logos used to distinguish source; design registration protects the visual appearance/shape of the product itself.' },
      { q: 'How long does design protection last?', a: 'Initially 10 years, extendable by a further 5 years, for a maximum of 15 years.' },
      { q: 'Can I register a design that\'s already been sold publicly?', a: 'No — the design must be new and not previously disclosed or used publicly before filing, so timing your application before launch matters.' },
    ],
  },

  DESIGN_OBJECTION: {
    intro: [
      'Objections during design examination usually relate to novelty concerns, classification issues, or incomplete representations of the design. A clear, timely response keeps your application on track.',
      "SolveTax reviews the examination report and prepares a response addressing the examiner's specific concerns.",
    ],
    eligibilityTitle: 'When does this apply?',
    eligibility: [
      'Your design application has received an examination objection',
      'The objection relates to novelty, classification or incomplete representations',
    ],
    documents: [
      'The examination report received',
      'Original design representations filed',
      'Any additional evidence of novelty or prior use dates',
    ],
    benefits: [
      'Keeps your design application alive instead of risking abandonment',
      'A clear, timely response often resolves the objection without further escalation',
    ],
    process: [
      { title: 'Objection reviewed', desc: 'We analyze the exact ground raised in the examination report.' },
      { title: 'Response prepared', desc: 'Arguments and, where needed, amended representations are prepared.' },
      { title: 'Filed within deadline', desc: 'Submitted to the Design Office within the given response window.' },
    ],
    faqs: [
      { q: 'What are common reasons for design objections?', a: "Lack of novelty compared to prior designs, unclear or insufficient representations, or incorrect classification of the article." },
      { q: 'How much time do I have to respond?', a: 'Response deadlines are set by the Design Office in the examination report — typically a few months, and we act promptly once received.' },
      { q: 'What happens if I don\'t respond to the objection?', a: 'The application is treated as abandoned if no response is filed within the given deadline, requiring a fresh filing to pursue registration.' },
      { q: 'Can I amend the design representations to overcome the objection?', a: 'In some cases, minor clarifications to the representations are allowed, though the core design itself cannot be substantively changed after filing.' },
    ],
  },

  TRADEMARK_PROTECTION: {
    intro: [
      "Registering your trademark is the first step — ongoing monitoring and enforcement is what actually protects your brand from being diluted or copied over time. Many businesses register a mark and then never watch for infringing use.",
      'SolveTax provides ongoing brand monitoring and can act quickly with cease-and-desist notices or opposition filings the moment infringing use is spotted.',
    ],
    eligibilityTitle: 'Who should use ongoing brand protection?',
    eligibility: [
      'Businesses with a registered trademark who want it actively defended, not just registered',
      'Growing brands entering e-commerce or new markets where copying is more likely',
    ],
    documents: [
      'Your trademark registration details',
      'List of marketplaces/regions where you want monitoring focused',
    ],
    benefits: [
      'Early detection of similar marks being filed or used by competitors',
      'Quick action (notices, opposition) before infringement becomes established',
      'Peace of mind that your registered brand asset is being actively protected',
    ],
    process: [
      { title: 'Monitoring set up', desc: 'We track new trademark filings and marketplace listings similar to your mark.' },
      { title: 'Alerts on potential conflicts', desc: 'You are notified whenever a potentially conflicting mark or use is detected.' },
      { title: 'Action taken', desc: 'Depending on severity, we help file an opposition, send a cease-and-desist notice, or advise on further legal action.' },
    ],
    faqs: [
      { q: 'How often is monitoring carried out?', a: 'We check new trademark journal publications and relevant marketplaces on a regular ongoing basis for your specific mark.' },
      { q: 'What happens if a conflicting mark is found?', a: 'We advise on the best course of action — typically an opposition if it is a new filing, or a cease-and-desist notice if it is already in use.' },
      { q: 'Is this a one-time or ongoing service?', a: "Brand protection is most effective as an ongoing service, since new conflicting filings and uses can appear at any time." },
    ],
  },

  // ===========================================================================
  // LICENSES / REGISTRATIONS
  // ===========================================================================
  MSME_REG: {
    intro: [
      'Udyam (MSME) registration is a free, fully online registration that officially classifies your business as a Micro, Small or Medium Enterprise — unlocking benefits like collateral-free loans, subsidies, delayed-payment protection and preference in government tenders.',
      'SolveTax completes your Udyam registration correctly on the first attempt, matching your investment and turnover to the right MSME category.',
    ],
    eligibilityTitle: 'Who can register as an MSME?',
    eligibility: [
      'Manufacturing or service businesses within the investment and turnover limits for Micro, Small or Medium classification',
      'Proprietorships, partnerships, LLPs, companies and other business entities',
      'Businesses wanting priority sector lending or collateral-free loan benefits',
    ],
    documents: [
      'Aadhaar of the proprietor/authorized signatory',
      'PAN of the business entity',
      'Bank account details',
      'Details of business activity (NIC code) and investment/turnover',
    ],
    benefits: [
      'Access to collateral-free loans under government credit schemes',
      'Protection against delayed payments from buyers, with interest on delays',
      'Price and purchase preference in many government tenders',
      'Subsidies on patent registration, ISO certification and more',
      'Lower interest rates on business loans from many banks',
    ],
    process: [
      { title: 'Share business & investment details', desc: 'Aadhaar, PAN and details of plant/machinery investment or turnover.' },
      { title: 'Application filed', desc: 'We complete the Udyam Registration portal application on your behalf.' },
      { title: 'Instant certificate', desc: 'The Udyam Registration Certificate with your unique number is generated immediately upon submission.' },
    ],
    timeline: 'Udyam registration is typically completed the same day — it is a self-declaration based online process.',
    mistakes: [
      { title: 'Entering the wrong NIC activity code', desc: 'An incorrect business activity code can misclassify your enterprise or cause issues when using the certificate for scheme eligibility later.' },
      { title: 'Not updating turnover figures annually', desc: 'Udyam registration pulls turnover from linked PAN/GST data — not keeping this current can misstate your MSME category.' },
    ],
    faqs: [
      { q: 'Is there a fee for Udyam/MSME registration?', a: 'The government charges no fee for Udyam registration — it is completely free to register directly on the portal.' },
      { q: 'What is the difference between Micro, Small and Medium?', a: 'Classification is based on investment in plant & machinery/equipment and annual turnover, with Micro having the lowest thresholds and Medium the highest.' },
      { q: 'Can I update my MSME details later?', a: 'Yes, your Udyam registration can be updated if your investment, turnover or business details change.' },
      { q: 'Does MSME registration help with GST or other compliance?', a: 'It is a separate registration but complements your other compliances — many benefits (loans, tenders) require your Udyam number specifically.' },
    ],
  },

  FSSAI_LICENSE: {
    intro: [
      'An FSSAI License (State or Central, depending on your scale) is required for larger food businesses — manufacturers, processors and businesses operating across states — going beyond the basic registration needed by small operators.',
      'SolveTax determines whether you need a State or Central license based on your turnover and business type, and files your application with the required documents.',
    ],
    eligibilityTitle: 'Who needs an FSSAI License (vs. basic registration)?',
    eligibility: [
      'Medium-sized food businesses with turnover above the basic registration threshold — State License',
      'Large manufacturers, importers/exporters and businesses operating in multiple states — Central License',
      'Food businesses with significant production capacity or storage',
    ],
    documents: [
      'Business PAN and identity proof of proprietor/directors',
      'Proof of premises ownership/lease and a layout plan',
      'List of food products to be manufactured/sold',
      'Water testing report (for manufacturing units) and food safety management plan',
    ],
    benefits: [
      'Legal compliance to manufacture, process, store, distribute or sell food',
      'Builds consumer trust with the FSSAI logo and license number on packaging',
      'Required for scaling distribution across states or exporting food products',
    ],
    process: [
      { title: 'Category & license type confirmed', desc: 'We determine State vs. Central license based on your turnover, business type and locations.' },
      { title: 'Application filed', desc: 'Form B is filed on the FoSCoS portal with your business and safety plan details.' },
      { title: 'Inspection (if applicable)', desc: 'Certain categories may require a premises inspection before approval.' },
      { title: 'License issued', desc: 'Your FSSAI license with a 14-digit number is issued once approved.' },
    ],
    timeline: 'Typically takes 30–60 days depending on license type and whether inspection is required.',
    mistakes: [
      { title: 'Applying for the wrong license type', desc: 'Applying for a State license while operating across multiple states (which needs Central license) is a common error that leads to rejection.' },
      { title: 'Missing the water testing report for manufacturing units', desc: 'Manufacturing categories specifically require a water potability report — an incomplete application without it gets held up at scrutiny.' },
    ],
    faqs: [
      { q: 'How is a license different from FSSAI registration?', a: 'Registration (Form A) is for small businesses below the turnover threshold; a license (Form B, State or Central) is for larger operations, with more detailed requirements.' },
      { q: 'How long is an FSSAI license valid?', a: 'You can choose validity from 1 to 5 years at the time of application, and it must be renewed before expiry.' },
      { q: 'Do I need a license for each location?', a: 'Generally yes — each manufacturing or storage unit typically needs its own license, while a central license can cover multi-state operations depending on the structure.' },
    ],
  },

  IEC_REG: {
    intro: [
      'An Import Export Code (IEC) is a mandatory 10-digit code from the DGFT required to import or export goods and services commercially from India. Without it, customs clearance and international payments for trade cannot proceed.',
      'SolveTax files your IEC application online and gets your code issued quickly, so you can start international trade without delay.',
    ],
    eligibilityTitle: 'Who needs an IEC?',
    eligibility: [
      'Any business or individual planning to import or export goods/services from India',
      'E-commerce exporters selling internationally through platforms like Amazon Global',
      'Service exporters receiving foreign currency payments (in most cases)',
    ],
    benefits: [
      'Legally required to clear shipments through customs',
      'Enables receiving/making international payments through banks',
      'One-time registration — no renewal or periodic filing required',
      'Access to export incentive schemes that require a valid IEC',
    ],
    documents: [
      'Business PAN',
      'Proof of business address',
      'Cancelled cheque or bank certificate for the current account',
      'Digital signature or Aadhaar for e-verification',
    ],
    process: [
      { title: 'Documents collected', desc: 'PAN, business address proof and bank details/cancelled cheque.' },
      { title: 'Application filed', desc: 'Filed online with the DGFT along with a digital signature or Aadhaar-based verification.' },
      { title: 'IEC issued', desc: 'Your 10-digit IEC is generated and emailed, typically within a day or two.' },
    ],
    timeline: 'IEC is usually issued within 1–3 working days of filing a complete application.',
    mistakes: [
      { title: 'Not updating IEC details annually', desc: 'IEC holders must confirm/update details every year on the DGFT portal — skipping this can lead to your IEC being deactivated.' },
    ],
    faqs: [
      { q: 'Is IEC required for service exports too?', a: 'In most cases yes, especially where foreign remittances are involved, though there are some specific RBI exemptions for individuals — we confirm your specific requirement.' },
      { q: 'Does IEC need annual renewal?', a: "It doesn't need renewal, but IEC holders must confirm/update their details annually on the DGFT portal even if nothing has changed." },
      { q: 'Can I use one IEC for multiple business locations?', a: 'Yes, an IEC is issued at the PAN level and covers all business locations under that same entity.' },
    ],
  },

  STARTUP_INDIA_REG: {
    intro: [
      'DPIIT recognition under the Startup India initiative unlocks tax exemptions, easier compliance, and access to government funds and schemes — but only if your application clearly establishes innovation and scalability, which is where many self-filed applications fall short.',
      'SolveTax prepares a strong DPIIT application that clearly articulates your innovation, and manages the recognition process end-to-end.',
    ],
    eligibilityTitle: 'Who is eligible for Startup India recognition?',
    eligibility: [
      'Private Limited Company, LLP or registered Partnership Firm',
      'Incorporated for less than 10 years',
      'Annual turnover not exceeding ₹100 crore in any financial year since incorporation',
      'Working towards innovation, improvement of products/services, or a scalable business model with high potential for employment/wealth creation',
      'Not formed by splitting up or reconstructing an existing business',
    ],
    documents: [
      'Certificate of Incorporation/Registration',
      'Details of directors/partners (PAN, ID proof)',
      'A brief write-up describing the innovation and business model',
      'Details of any funding received, patents filed, or awards (if applicable)',
    ],
    benefits: [
      'Income tax exemption for 3 consecutive years (Section 80-IAC, subject to conditions)',
      'Exemption from angel tax on investments above fair market value (subject to conditions)',
      'Self-certification compliance under select labor and environment laws',
      'Faster patent examination and up to 80% rebate on patent filing fees',
      'Access to the Startup India Seed Fund and government tenders reserved for startups',
    ],
    process: [
      { title: 'Eligibility check', desc: 'We confirm your entity type, age and turnover meet DPIIT criteria.' },
      { title: 'Application drafted', desc: 'A clear write-up of your innovation and business model is prepared for the application.' },
      { title: 'Filed on Startup India portal', desc: 'Application submitted along with incorporation and supporting documents.' },
      { title: 'Recognition certificate', desc: 'Once approved, you receive your DPIIT recognition number and certificate.' },
    ],
    timeline: 'DPIIT recognition typically takes 2–4 weeks, depending on query resolution.',
    mistakes: [
      { title: 'Writing a generic, non-specific innovation description', desc: 'Applications that just describe the business idea without clearly articulating innovation or scalability are the most common reason for a query or rejection.' },
      { title: 'Applying after crossing the 10-year or ₹100 crore turnover limit', desc: 'Eligibility is checked strictly against these thresholds — applying without confirming them first wastes the application cycle.' },
    ],
    faqs: [
      { q: 'Does DPIIT recognition mean automatic tax exemption?', a: 'No — recognition is a prerequisite, but the income tax exemption under Section 80-IAC requires a separate application and approval from the Inter-Ministerial Board.' },
      { q: 'Can a partnership firm apply?', a: 'Yes, registered partnership firms are eligible alongside Private Limited Companies and LLPs.' },
      { q: 'What if my application gets a query or rejection?', a: 'We help you respond to clarification queries or reapply with stronger articulation of your innovation if the first attempt is rejected.' },
    ],
  },

  TRADE_LICENSE: {
    intro: [
      'A trade license from your local municipal corporation permits you to legally operate a specific business at a specific location — required across most cities before you open a shop, restaurant, warehouse or manufacturing unit.',
      'SolveTax identifies the exact license category for your business and municipal body, and manages the application and inspection process.',
    ],
    eligibilityTitle: 'Who needs a trade license?',
    eligibility: [
      'Shops, restaurants and food outlets operating from a physical premises',
      'Manufacturing units, warehouses and godowns',
      'Any business activity that the local municipal body classifies as requiring a license',
    ],
    documents: [
      'Proof of ownership/lease of business premises',
      'Identity and address proof of the applicant',
      'PAN and passport-size photograph',
      'NOC from the landlord, if premises are rented',
    ],
    benefits: [
      'Legal authorization to operate at your specific premises',
      'Avoids penalties and forced closure for operating without municipal permission',
    ],
    process: [
      { title: 'Category identified', desc: 'We determine the correct trade category and applicable municipal body for your location.' },
      { title: 'Application filed', desc: 'Filed with the municipal corporation along with premises and identity documents.' },
      { title: 'Inspection (if required)', desc: 'Some categories require a site inspection by municipal officials.' },
      { title: 'License issued', desc: 'The trade license is issued, typically valid for one year and renewable annually.' },
    ],
    timeline: 'Processing time varies by municipal corporation, typically 15–30 days.',
    faqs: [
      { q: 'Does a trade license need annual renewal?', a: 'Yes, trade licenses are generally valid for one financial year and must be renewed before expiry.' },
      { q: 'Is a trade license the same as GST or FSSAI registration?', a: 'No — it is a separate municipal permission to operate at a location, in addition to any GST, FSSAI or other sector-specific registrations you may need.' },
      { q: 'What happens if I operate without a trade license?', a: 'Municipal authorities can levy penalties or order closure of the premises until the license is obtained.' },
    ],
  },

  FSSAI_REG: {
    intro: [
      'FSSAI Registration (Form A) is the basic food safety registration for small food businesses — home kitchens, small eateries, petty food manufacturers and retailers — with turnover typically below ₹12 lakh per year.',
      'SolveTax completes your FSSAI registration quickly online, so you can legally start or continue your food business.',
    ],
    eligibilityTitle: 'Who needs FSSAI Registration?',
    eligibility: [
      'Small food businesses with annual turnover up to ₹12 lakh',
      'Home-based food businesses, small eateries, petty retailers and hawkers',
      'Businesses that don\'t yet require a full State or Central FSSAI License',
    ],
    documents: [
      'Photo ID proof of the food business operator',
      'Passport-size photograph',
      'Business address proof',
      'A declaration of the food category being handled',
    ],
    benefits: [
      'Legal recognition to sell food commercially, including via delivery apps',
      'Low-cost, fast registration suited to small and home-based food businesses',
    ],
    process: [
      { title: 'Details collected', desc: 'Business details, ID proof and photograph of the food business operator.' },
      { title: 'Form A filed', desc: 'Application filed on the FoSCoS portal.' },
      { title: 'Registration certificate', desc: 'Issued with a 14-digit registration number, typically within a couple of weeks.' },
    ],
    timeline: 'Typically issued within 7–15 working days for straightforward applications.',
    faqs: [
      { q: 'What if my turnover grows beyond ₹12 lakh?', a: 'You will need to upgrade to a State FSSAI License once you cross the threshold — SolveTax can handle that transition for you.' },
      { q: 'Is FSSAI registration required for home-based food businesses?', a: 'Yes, even home kitchens selling food commercially (including via delivery apps) need FSSAI registration.' },
      { q: 'How long is the registration valid?', a: 'You can choose validity between 1 and 5 years at the time of application.' },
    ],
  },

  HALAL_CERT: {
    intro: [
      'Halal certification confirms that a food or consumer product complies with Islamic dietary law, opening access to Halal-conscious consumers domestically and export markets across the Middle East, Southeast Asia and beyond.',
      'SolveTax coordinates your Halal certification application with the appropriate certifying body based on your product and target export market.',
    ],
    eligibilityTitle: 'Who should get Halal certified?',
    eligibility: [
      'Food and beverage manufacturers targeting Halal-conscious consumers',
      'Exporters shipping to Halal-certification-requiring markets (GCC, Malaysia, Indonesia, etc.)',
      'Cosmetics, pharmaceutical and other consumer goods manufacturers seeking Halal assurance',
    ],
    documents: [
      'Full list of ingredients and suppliers',
      'Manufacturing process flow chart',
      'Business registration and FSSAI license (for food businesses)',
      'Supplier Halal declarations, where available',
    ],
    benefits: [
      'Opens access to Halal-conscious domestic and export markets',
      'A recognized certification mark builds consumer trust',
    ],
    process: [
      { title: 'Product & ingredient review', desc: 'Your ingredients, sourcing and processing methods are reviewed for Halal compliance.' },
      { title: 'Documentation prepared', desc: 'Supplier declarations and process documentation are compiled.' },
      { title: 'Audit', desc: 'The certifying body typically conducts a facility audit as part of certification.' },
      { title: 'Certificate issued', desc: 'Halal certification is issued once compliance is confirmed.' },
    ],
    timeline: 'Typically 4–8 weeks depending on the certifying body\'s audit scheduling and your documentation readiness.',
    faqs: [
      { q: 'Which certifying bodies are recognized internationally?', a: 'Recognition varies by destination market — we help identify a certifying body accepted in your specific target export markets.' },
      { q: 'Does Halal certification need renewal?', a: 'Yes, it is typically valid for a fixed period (often 1–2 years) and requires renewal with a fresh audit.' },
      { q: 'Is Halal certification only for food products?', a: 'No, it also applies to cosmetics, pharmaceuticals and other consumer products where Halal compliance matters to the target market.' },
    ],
  },

  ICEGATE_REG: {
    intro: [
      'ICEGATE registration connects your business to the Indian Customs electronic gateway — needed to file shipping bills, bills of entry, and other customs documents electronically for import/export clearance.',
      'SolveTax completes your ICEGATE registration and digital signature setup, so your customs filings go through without delay.',
    ],
    eligibilityTitle: 'Who needs ICEGATE registration?',
    eligibility: [
      'Importers and exporters who need to file customs documents directly',
      'Customs House Agents and freight forwarders handling clearance on behalf of clients',
      'Businesses holding a valid IEC looking to self-file customs documentation',
    ],
    documents: [
      'Valid IEC certificate',
      'Class 3 Digital Signature Certificate',
      'Authorized signatory\'s identity proof',
      'Bank account details linked to the IEC',
    ],
    benefits: [
      'Direct control and visibility over your own customs filings',
      'Faster shipment clearance without depending solely on a broker\'s access',
    ],
    process: [
      { title: 'IEC & DSC verified', desc: 'A valid IEC and Class 3 Digital Signature Certificate are prerequisites for registration.' },
      { title: 'Application filed', desc: 'Registration is completed on the ICEGATE portal with business and authorized signatory details.' },
      { title: 'Access credentials issued', desc: 'Login credentials for filing customs documents are issued once verified.' },
    ],
    timeline: 'Typically completed within a few working days once IEC and DSC are in place.',
    faqs: [
      { q: 'Do I need ICEGATE registration if I use a customs broker?', a: 'Not necessarily — many businesses rely on their customs house agent\'s ICEGATE access, but direct registration gives you more control and visibility.' },
      { q: 'Is a digital signature mandatory for ICEGATE?', a: 'Yes, a Class 3 DSC is required to file most documents electronically through ICEGATE.' },
      { q: 'What can I file once registered on ICEGATE?', a: 'Shipping bills, bills of entry, and various other customs declarations and correspondence directly with Indian Customs.' },
      { q: 'How long does ICEGATE registration take?', a: 'Typically a few working days once your IEC and DSC are ready and verified.' },
    ],
  },

  LEI_CODE: {
    intro: [
      'A Legal Entity Identifier (LEI) is a unique 20-character global reference code required for entities involved in large-value financial transactions, derivatives trading, or certain RBI-regulated activities.',
      'SolveTax obtains your LEI code from an accredited Local Operating Unit, handling the documentation and verification process.',
    ],
    eligibilityTitle: 'Who needs an LEI code?',
    eligibility: [
      'Entities undertaking large-value transactions above RBI-specified thresholds',
      'Businesses participating in derivative markets or certain capital market transactions',
      'Non-individual borrowers with large credit exposures, per RBI requirements',
    ],
    documents: [
      'Certificate of Incorporation/Registration',
      'PAN of the entity',
      'Details of ownership/parent-subsidiary structure',
      'Authorized signatory\'s ID proof',
    ],
    benefits: [
      'Meets RBI-mandated requirements for large-value transactions',
      'A single global identifier recognized across financial systems worldwide',
    ],
    process: [
      { title: 'Entity details verified', desc: 'Legal name, registration details and ownership structure are verified.' },
      { title: 'Application filed', desc: 'Submitted through an accredited Local Operating Unit (LOU) in India.' },
      { title: 'LEI issued', desc: 'The unique 20-character LEI code is issued once verification is complete.' },
    ],
    timeline: 'LEI codes are typically issued within a few working days of submitting complete documentation.',
    faqs: [
      { q: 'Does the LEI code need renewal?', a: 'Yes, LEI codes require annual renewal to remain valid and active in the Global LEI System.' },
      { q: 'Is LEI mandatory for all businesses?', a: 'No — it is specifically required for entities crossing RBI-specified transaction thresholds or participating in certain regulated markets.' },
      { q: 'What happens if my LEI lapses?', a: 'A lapsed LEI can block certain regulated transactions until it is renewed — we track your renewal date to avoid this.' },
      { q: 'How is the LEI different from a PAN or CIN?', a: 'PAN and CIN are India-specific identifiers; the LEI is a globally standardized 20-character code recognized across international financial systems.' },
    ],
  },

  ISO_REG: {
    intro: [
      'ISO certification (9001 for quality management, 22000 for food safety, 27001 for information security, and others) demonstrates that your business follows internationally recognized standards — often a requirement for larger contracts, tenders and exports.',
      'SolveTax identifies the right ISO standard for your business and coordinates documentation, gap assessment and certification audit with an accredited certifying body.',
    ],
    eligibilityTitle: 'Who should get ISO certified?',
    eligibility: [
      'Businesses bidding for government or enterprise tenders that require ISO certification',
      'Manufacturers and service providers wanting to demonstrate quality/process standards',
      'Exporters where international buyers expect ISO-certified suppliers',
    ],
    documents: [
      'Business registration proof',
      'Process documentation for the relevant standard (quality manual, SOPs)',
      'List of products/services covered under certification scope',
    ],
    benefits: [
      'Improved credibility with clients, partners and tender authorities',
      'Better internal process consistency and quality control',
      'Often required to qualify for larger contracts and export opportunities',
      'MSMEs may be eligible for government subsidy schemes covering ISO certification costs',
    ],
    process: [
      { title: 'Standard identified', desc: 'We determine which ISO standard (9001, 22000, 27001, etc.) fits your business.' },
      { title: 'Documentation & gap assessment', desc: 'Your current processes are reviewed against the standard\'s requirements.' },
      { title: 'Certification audit', desc: 'An accredited certifying body conducts the assessment audit.' },
      { title: 'Certificate issued', desc: 'ISO certification is issued upon successful audit completion.' },
    ],
    mistakes: [
      { title: 'Documenting processes that aren\'t actually followed', desc: 'Certification requires your documented process and actual practice to match — a mismatch discovered during audit delays certification.' },
    ],
    faqs: [
      { q: 'Which ISO standard is right for my business?', a: 'ISO 9001 (quality management) suits most businesses broadly; food businesses often need ISO 22000, and IT/data-focused businesses often pursue ISO 27001 — we help you choose.' },
      { q: 'How long is ISO certification valid?', a: 'Typically 3 years, with periodic surveillance audits required to maintain it.' },
      { q: 'Is ISO certification mandatory?', a: "It's generally voluntary, but often a practical requirement to win certain tenders and enterprise or export contracts." },
    ],
  },

  PF_REG: {
    intro: [
      'PF (Provident Fund) registration with the EPFO is required once your establishment reaches the employee threshold, setting up the framework for monthly employee and employer contributions.',
      'SolveTax completes your establishment\'s PF registration and gets your PF code (establishment ID) issued, ready for monthly filing.',
    ],
    eligibilityTitle: 'Who needs PF registration?',
    eligibility: [
      'Establishments with 20 or more employees (mandatory)',
      'Smaller establishments choosing to register voluntarily',
    ],
    documents: [
      'Business registration certificate (incorporation/GST/Shop Act)',
      'PAN of the establishment',
      'List of employees with salary details',
      'Bank account details of the establishment',
    ],
    benefits: [
      'Sets up the framework to offer employees a valuable retirement benefit',
      'Establishes compliance readiness before the mandatory threshold is crossed',
    ],
    process: [
      { title: 'Establishment details collected', desc: 'Business registration documents, PAN and employee details.' },
      { title: 'Application filed', desc: 'Registration completed on the EPFO Unified Portal.' },
      { title: 'PF code issued', desc: 'Your unique establishment code is generated, enabling monthly contribution filing.' },
    ],
    timeline: 'Registration is usually completed within a few days of submitting complete documentation.',
    faqs: [
      { q: 'What happens after PF registration — do I need to file every month?', a: 'Yes, once registered you must file monthly PF returns and deposit contributions by the 15th — SolveTax also offers ongoing PF Filing as a separate service.' },
      { q: 'Can a business with fewer than 20 employees register voluntarily?', a: 'Yes, voluntary registration is allowed and can be a good benefit to offer employees even before it becomes mandatory.' },
      { q: 'How long does PF registration take?', a: 'Typically a few working days once your establishment documents and employee details are ready.' },
      { q: 'Is PF registration linked to a single location or the whole company?', a: 'It is generally registered at the establishment/company level and can cover multiple work locations under one PF code, depending on your structure.' },
    ],
  },

  ESI_REG: {
    intro: [
      'ESI (Employee State Insurance) registration is required once your establishment meets the employee threshold, enabling medical and cash benefits for eligible employees earning within the wage ceiling.',
      'SolveTax completes your ESI registration and obtains your establishment code, so you can start covering eligible employees.',
    ],
    eligibilityTitle: 'Who needs ESI registration?',
    eligibility: [
      'Establishments with 10 or more employees in most states (threshold varies)',
      'Applicable once you have employees earning within the ESI wage ceiling (currently ₹21,000/month)',
    ],
    documents: [
      'Business registration certificate',
      'PAN of the establishment',
      'List of employees with wage details',
      'Bank account details of the establishment',
    ],
    benefits: [
      'Gives eligible employees access to medical and cash benefits',
      'Meets your statutory obligation once employee/wage thresholds are crossed',
    ],
    process: [
      { title: 'Establishment details collected', desc: 'Business registration proof, PAN and employee wage details.' },
      { title: 'Application filed', desc: 'Registration completed on the ESIC portal.' },
      { title: 'Establishment code issued', desc: 'Your unique ESIC code is generated, ready for monthly contribution filing.' },
    ],
    timeline: 'Registration is usually completed within a few days of submitting complete documentation.',
    faqs: [
      { q: 'What benefits does ESI provide employees?', a: 'Medical treatment, sickness benefit, maternity benefit and disability benefits for covered employees and their dependents.' },
      { q: 'Do I need to file monthly after registration?', a: 'Yes, monthly ESI contributions must be filed and paid by the 15th of the following month — see our ESI Filing service for ongoing compliance.' },
      { q: 'How long does ESI registration take?', a: 'Typically a few working days once your establishment and employee wage details are submitted.' },
      { q: 'Do all employees need to be covered under ESI?', a: 'Only those earning within the statutory wage ceiling — employees above it are excluded from ESI coverage, though other benefits can be offered separately.' },
    ],
  },

  PROF_TAX_REG: {
    intro: [
      'Professional Tax is a state-level tax on salaried employees and professionals, and most states require employers to register and deduct/deposit it on behalf of employees, alongside a separate registration for self-employed professionals.',
      'SolveTax handles both your employer registration (to deduct from salaries) and enrollment (for self-employed professionals), based on your state\'s specific rules.',
    ],
    eligibilityTitle: 'Who needs Professional Tax registration?',
    eligibility: [
      'Employers with staff on payroll, in states where professional tax applies',
      'Self-employed professionals (CAs, doctors, consultants, etc.) in applicable states',
      'Requirements and rates vary by state — not all states levy professional tax',
    ],
    documents: [
      'Business registration proof and PAN',
      'Employee salary details (for employer registration)',
      'Professional qualification proof (for self-employed enrollment)',
    ],
    benefits: [
      'Avoids state-level penalties for non-registration where professional tax applies',
      'Keeps payroll compliance complete alongside PF/ESI',
    ],
    process: [
      { title: 'State applicability confirmed', desc: 'We check whether professional tax applies in your state and which registration type you need.' },
      { title: 'Application filed', desc: 'Filed with the state professional tax department along with business/employee details.' },
      { title: 'Registration certificate issued', desc: 'You receive your registration/enrollment certificate to begin compliance.' },
    ],
    timeline: 'Typically completed within 7–15 working days, depending on the state department.',
    faqs: [
      { q: 'Is professional tax applicable in every state?', a: 'No, it varies by state — some states (like Maharashtra, Karnataka, West Bengal) levy it while others don\'t. We confirm your specific state requirement.' },
      { q: 'What is the difference between registration and enrollment?', a: 'Registration is for employers deducting tax from employee salaries; enrollment is for self-employed professionals paying their own professional tax directly.' },
      { q: 'What happens if I don\'t register where professional tax applies?', a: 'Most states levy a penalty and interest for non-registration or non-payment once the obligation applies to your business.' },
      { q: 'How often is professional tax paid?', a: 'This varies by state — most require monthly or annual payment for employers, and annual payment for self-employed professionals.' },
    ],
  },

  RCMC_REG: {
    intro: [
      'A Registration-cum-Membership Certificate (RCMC) from your relevant Export Promotion Council is required to avail benefits and incentives under India\'s Foreign Trade Policy, and is often needed alongside your IEC for certain export schemes.',
      'SolveTax identifies the correct Export Promotion Council for your product category and files your RCMC application.',
    ],
    eligibilityTitle: 'Who needs an RCMC?',
    eligibility: [
      'Exporters wanting to avail benefits/incentives under the Foreign Trade Policy',
      'Businesses applying for export scheme benefits that require RCMC as a supporting document',
    ],
    documents: [
      'Valid IEC certificate',
      'Business registration proof',
      'Details of products/services exported',
      'Bank certificate/self-declaration of export performance',
    ],
    benefits: [
      'Unlocks eligibility for Foreign Trade Policy incentive schemes',
      'Recognized proof of your export sector membership',
    ],
    process: [
      { title: 'Council identified', desc: 'We determine the correct Export Promotion Council for your specific product/service category.' },
      { title: 'Application filed', desc: 'Submitted to the council along with IEC and business details.' },
      { title: 'RCMC issued', desc: 'Certificate issued, typically valid for 5 years.' },
    ],
    timeline: 'Typically issued within 1–2 weeks of submitting a complete application to the relevant council.',
    faqs: [
      { q: 'Is RCMC mandatory for all exporters?', a: 'It is specifically required for availing certain export incentive schemes under the Foreign Trade Policy, not for exporting itself.' },
      { q: 'How long is an RCMC valid?', a: 'Generally 5 financial years, after which renewal is required.' },
      { q: 'How do I know which Export Promotion Council applies to me?', a: 'It depends on your specific product or service category — we identify the correct council for you as part of the application process.' },
      { q: 'What documents does RCMC support when applying for export incentives?', a: 'It serves as proof of your registration with the relevant sector body, commonly required alongside your IEC for Foreign Trade Policy scheme applications.' },
    ],
  },

  TN_RERA_AGENT_REG: {
    intro: [
      'Real estate agents operating in Tamil Nadu must register with TN RERA before facilitating property transactions — operating without this registration can attract penalties under the RERA Act.',
      'SolveTax prepares and files your agent registration application with TN RERA, including the required documentation.',
    ],
    eligibilityTitle: 'Who needs this registration?',
    eligibility: [
      'Individuals and firms acting as real estate agents/brokers in Tamil Nadu',
      'Anyone facilitating sale, purchase or lease of property as an intermediary in the state',
    ],
    documents: [
      'Identity and address proof of the agent/firm',
      'PAN and passport-size photograph',
      'Business registration proof, if applying as a firm',
    ],
    benefits: [
      'Legal authorization to operate as a real estate agent in Tamil Nadu',
      'Avoids penalties for facilitating transactions without valid registration',
    ],
    process: [
      { title: 'Documents collected', desc: 'Identity proof, address proof and business details of the agent/firm.' },
      { title: 'Application filed', desc: 'Submitted on the TN RERA portal with the prescribed fee.' },
      { title: 'Registration certificate issued', desc: 'A unique RERA agent registration number is issued upon approval.' },
    ],
    timeline: 'Typically processed within a few weeks of submitting a complete application.',
    faqs: [
      { q: 'What happens if I operate as an agent without RERA registration?', a: 'Unregistered agents can face penalties under the RERA Act for facilitating transactions without valid registration.' },
      { q: 'Does this registration need renewal?', a: 'Yes, RERA agent registration is valid for a specified period and needs renewal before expiry — we track this for you.' },
      { q: 'Can a firm register, or only individual agents?', a: 'Both individuals and firms acting as real estate agents/brokers can register with TN RERA.' },
      { q: 'How long does the registration process take?', a: 'Typically a few weeks once complete documentation is submitted to the TN RERA portal.' },
    ],
  },

  REG_12A_80G: {
    intro: [
      "12A registration exempts your NGO/trust's income from tax, while 80G registration lets your donors claim a tax deduction on their contributions — together, they make your organization far more attractive to donors.",
      'SolveTax files both registrations together, preparing the required trust deed, activity reports and financial documentation.',
    ],
    eligibilityTitle: 'Who should apply?',
    eligibility: [
      'Registered trusts, societies and Section 8 companies engaged in charitable activity',
      'Organizations seeking income tax exemption and wanting to offer donors tax deduction benefits',
    ],
    documents: [
      'Trust deed / Memorandum of Association',
      'PAN of the organization',
      'Details of trustees/office bearers',
      'Activity reports and financial statements of past years, if already operational',
    ],
    benefits: [
      'Income of the trust/NGO becomes exempt from income tax under Section 12A',
      'Donors can claim deduction on donations under Section 80G, encouraging larger contributions',
      'Builds credibility with institutional donors and CSR-funding companies',
    ],
    process: [
      { title: 'Documentation prepared', desc: 'Trust deed/MOA, activity reports and financial statements are compiled.' },
      { title: 'Application filed', desc: 'Filed online with the Income Tax Department (Form 10A/10AB as applicable).' },
      { title: 'Provisional/final registration granted', desc: 'Registration is granted, often provisionally first, then on a regular basis after review of activities.' },
    ],
    timeline: 'Provisional registration is typically granted within 1–3 months of a complete application.',
    faqs: [
      { q: 'Can I apply for 12A and 80G together?', a: 'Yes, both are commonly filed together since 80G registration requires the organization to already hold (or simultaneously apply for) 12A registration.' },
      { q: 'Does this registration need renewal?', a: 'Yes, under the current regime, registration is granted for a fixed period and must be renewed before expiry.' },
      { q: 'Do all NGOs automatically get 80G approval?', a: 'No, the Income Tax Department reviews the genuineness and activities of the organization before granting approval.' },
    ],
  },

  REG_12A: {
    intro: [
      "12A registration is what makes your trust or NGO's income exempt from income tax — without it, your organization is taxed like any other entity despite being charitable in purpose.",
      'SolveTax prepares your application with the required trust documentation and activity evidence, and manages the filing.',
    ],
    eligibilityTitle: 'Who should apply for 12A?',
    eligibility: [
      'Newly registered trusts, societies and Section 8 companies with charitable objects',
      'Existing organizations that have not yet obtained tax-exempt status',
    ],
    documents: [
      'Trust deed / Memorandum of Association',
      'PAN of the organization',
      'Details of trustees and their KYC',
      'Details of activities carried out or planned',
    ],
    benefits: [
      'Income of the organization becomes exempt from income tax',
      'A prerequisite for 80G approval, wider government grants and FCRA eligibility',
    ],
    process: [
      { title: 'Documents prepared', desc: 'Trust deed/MOA, PAN, and details of activities carried out or planned.' },
      { title: 'Form 10A/10AB filed', desc: 'Application filed online with the Income Tax Department.' },
      { title: 'Registration granted', desc: 'Once satisfied, the department grants registration — often provisional initially for new organizations.' },
    ],
    timeline: 'Typically granted within 1–3 months of filing a complete application.',
    faqs: [
      { q: 'Is 12A registration mandatory for NGOs?', a: 'It is not legally mandatory to operate, but without it your organization\'s income is taxable — most genuine charitable organizations apply for this exemption.' },
      { q: 'Should I apply for 80G at the same time?', a: 'Most organizations do, since 80G lets your donors claim a deduction, making donations far more attractive — we typically recommend filing both together.' },
      { q: 'Does 12A registration need renewal?', a: 'Yes, under the current regime registration is granted for a fixed validity period and must be renewed before it expires.' },
      { q: 'What if my organization is newly formed with no activity history yet?', a: 'New organizations can still apply and typically receive provisional registration, which converts to regular registration after review of actual activities.' },
    ],
  },

  REG_80G: {
    intro: [
      "80G registration lets donors to your trust or NGO claim a tax deduction on their contribution — a significant factor in attracting individual and corporate donors, including CSR funding.",
      'SolveTax prepares and files your 80G application, ensuring your activities and documentation meet the requirements for approval.',
    ],
    eligibilityTitle: 'Who should apply for 80G?',
    eligibility: [
      'Trusts, societies and Section 8 companies already holding (or applying for) 12A registration',
      'Organizations wanting to attract donations by offering donors a tax deduction',
    ],
    documents: [
      '12A registration certificate',
      'Trust deed / Memorandum of Association',
      'Activity reports and financial statements',
      'Details of trustees/office bearers',
    ],
    benefits: [
      'Donors can deduct their contribution from taxable income, encouraging larger donations',
      'Makes your organization more attractive to CSR-funded corporate donors',
    ],
    process: [
      { title: 'Documentation prepared', desc: 'Trust deed, 12A registration proof, activity reports and financials.' },
      { title: 'Application filed', desc: 'Form 10A/10AB filed with the Income Tax Department.' },
      { title: 'Approval granted', desc: 'Once approved, your organization can issue 80G receipts to donors.' },
    ],
    timeline: 'Typically granted within 1–3 months of filing a complete application.',
    faqs: [
      { q: 'Do I need 12A registration before applying for 80G?', a: 'Yes, 80G approval requires the organization to already hold or be simultaneously applying for 12A registration.' },
      { q: 'How much deduction can a donor claim?', a: 'Depending on the specific approval, donors can typically claim 50% or 100% deduction on their donation amount, subject to overall limits under the Income Tax Act.' },
      { q: 'Does 80G registration need renewal?', a: 'Yes, it is granted for a fixed validity period under the current regime and must be renewed before expiry to keep issuing valid 80G receipts.' },
      { q: 'What do donors need to claim the deduction?', a: 'A valid 80G receipt from your organization along with your registration details, which they submit with their own income tax return.' },
    ],
  },

  BARCODE_REG: {
    intro: [
      'A GS1 barcode registration gives your products a globally unique barcode number, required by most retail chains, e-commerce marketplaces and export buyers before they will list your product.',
      'SolveTax handles your GS1 barcode registration and helps you get product-level barcodes assigned correctly.',
    ],
    eligibilityTitle: 'Who needs barcode registration?',
    eligibility: [
      'Manufacturers and brands selling through retail stores or supermarkets',
      'E-commerce sellers whose marketplace requires a GS1 barcode for listing',
      'Exporters whose international buyers require standard barcoding',
    ],
    documents: [
      'Business registration proof',
      'PAN of the business',
      'List of products requiring individual barcodes',
    ],
    benefits: [
      'Required to list products on most retail chains and e-commerce marketplaces',
      'Globally recognized numbering that international buyers trust',
    ],
    process: [
      { title: 'Company details collected', desc: 'Business registration proof and details of products to be barcoded.' },
      { title: 'GS1 membership registration', desc: 'We register your company with GS1 India to obtain your unique company prefix.' },
      { title: 'Product barcodes generated', desc: 'Individual product barcodes are generated under your company prefix, ready for packaging.' },
    ],
    timeline: 'Typically completed within a few working days of submitting complete documentation.',
    faqs: [
      { q: 'Does barcode registration need renewal?', a: 'Yes, GS1 barcode registration typically requires periodic renewal of your membership to keep your barcodes valid.' },
      { q: 'Can I get barcodes for multiple products under one registration?', a: 'Yes, your company prefix allows you to generate barcodes for as many individual products as you need.' },
      { q: 'Do I need a barcode to sell on e-commerce marketplaces?', a: 'Most major marketplaces require a valid GS1 barcode before they will list your product, especially for branded/manufactured goods.' },
      { q: 'How long does barcode registration take?', a: 'Typically a few working days once your company and product details are submitted to GS1 India.' },
    ],
  },

  BIS_REG: {
    intro: [
      'BIS (Bureau of Indian Standards) registration/certification is mandatory for specific product categories — from electronics to steel to certain packaged goods — confirming they meet Indian quality and safety standards before sale.',
      'SolveTax identifies whether your product falls under a mandatory BIS scheme and manages the testing and certification process.',
    ],
    eligibilityTitle: 'Who needs BIS registration?',
    eligibility: [
      'Manufacturers of products under mandatory BIS certification schemes (many electronics, cables, toys, cement, etc.)',
      'Importers of notified products required to carry the BIS/ISI mark',
    ],
    documents: [
      'Business registration and manufacturing premises details',
      'Product technical specifications',
      'Factory test setup/quality control details',
    ],
    benefits: [
      'Legal compliance for products under mandatory certification',
      'The ISI mark signals safety and quality to consumers and buyers',
    ],
    process: [
      { title: 'Applicability check', desc: 'We confirm whether your specific product falls under a mandatory BIS scheme.' },
      { title: 'Sample testing', desc: 'Product samples are tested at a BIS-recognized laboratory against the relevant standard.' },
      { title: 'License application', desc: 'Filed with BIS along with test reports and manufacturing details.' },
      { title: 'License granted', desc: 'The BIS/ISI mark license is granted upon successful compliance verification.' },
    ],
    faqs: [
      { q: 'Is BIS certification mandatory for all products?', a: "No, only products under specific mandatory certification schemes require it — many everyday products don't, while items like certain electronics and cables do." },
      { q: 'How long does BIS certification take?', a: 'It varies significantly by product category and testing requirements — typically a few months given lab testing timelines.' },
      { q: 'What happens if I sell a notified product without BIS certification?', a: 'It can attract penalties and seizure of non-compliant stock under the BIS Act — checking applicability before launch avoids this risk entirely.' },
      { q: 'Does BIS certification need renewal?', a: 'Yes, licenses are typically granted for a fixed period and need periodic renewal along with continued compliance verification.' },
    ],
  },

  CERT_INCUMBENCY: {
    intro: [
      "A Certificate of Incumbency officially confirms your company's current directors, officers and shareholders as of a given date — often requested by foreign banks, investors or business partners as proof of who currently controls the company.",
      'SolveTax prepares this certificate based on your current MCA records and company registers, certified appropriately for its intended use.',
    ],
    eligibilityTitle: 'When do you need this certificate?',
    eligibility: [
      'Opening a bank account or investment account abroad',
      'International business partners or investors requesting proof of current company officers',
      'Certain cross-border legal or regulatory filings',
    ],
    documents: [
      'Certificate of Incorporation and MCA master data',
      'List of current directors and shareholders',
      'Details of the requesting party/jurisdiction, to match the expected format',
    ],
    benefits: [
      'Satisfies foreign institution requirements quickly, without back-and-forth delay',
      'Prepared in the format expected internationally, including notarization/apostille where needed',
    ],
    process: [
      { title: 'Current records verified', desc: 'We confirm your current directors, shareholders and officers against MCA records.' },
      { title: 'Certificate drafted', desc: 'Prepared in the standard format expected by the requesting party/jurisdiction.' },
      { title: 'Certified & delivered', desc: 'Signed and certified as needed (notarization/apostille where required for international use).' },
    ],
    faqs: [
      { q: 'Is this the same as a Certificate of Good Standing?', a: 'They serve related but distinct purposes — a Certificate of Incumbency confirms who currently holds office, while good standing confirms the company is compliant and active. We can advise on which one your request needs.' },
      { q: 'Do I need to notarize or apostille this certificate?', a: 'It depends on where it will be used — many foreign institutions require notarization and/or apostille, which we can help coordinate.' },
      { q: 'How current does the information need to be?', a: 'It reflects your company\'s officers and shareholders as of the date requested, verified directly against current MCA records at the time of issue.' },
      { q: 'How long does it take to prepare?', a: 'Typically a few working days, longer if notarization or apostille is required for use abroad.' },
    ],
  },

  DARPAN_REG: {
    intro: [
      'NGO Darpan registration on the NITI Aayog portal is required for NGOs seeking to partner with government schemes, receive government grants, or apply for FCRA (foreign contribution) registration.',
      'SolveTax completes your NGO Darpan registration and helps you get the unique ID needed for government scheme eligibility.',
    ],
    eligibilityTitle: 'Who needs Darpan registration?',
    eligibility: [
      'NGOs, trusts and societies wanting to apply for government grants or scheme partnerships',
      'Organizations seeking or renewing FCRA registration (Darpan ID is a prerequisite)',
    ],
    documents: [
      'Trust/society/Section 8 company registration certificate',
      'PAN of the organization',
      'Details of office bearers with ID proof',
    ],
    benefits: [
      'Prerequisite for accessing government grants and scheme partnerships',
      'Required for FCRA (foreign contribution) registration eligibility',
    ],
    process: [
      { title: 'Organization details collected', desc: 'Registration certificate, PAN, and details of office bearers.' },
      { title: 'Application filed', desc: 'Submitted on the NGO Darpan portal with supporting documents.' },
      { title: 'Unique ID issued', desc: 'A unique Darpan ID is generated once the application is verified.' },
    ],
    faqs: [
      { q: 'Is Darpan registration mandatory for all NGOs?', a: "It's not mandatory to operate, but it is a prerequisite for accessing many government grants, schemes and for FCRA registration." },
      { q: 'How long does Darpan registration take?', a: "It's typically a straightforward online process completed within a few days of submitting complete documentation." },
      { q: 'Do I need Darpan registration for FCRA?', a: 'Yes, a valid Darpan ID is a mandatory prerequisite before applying for FCRA (foreign contribution) registration.' },
      { q: 'Does the Darpan ID need renewal?', a: 'No renewal is required, but organization details should be kept updated on the portal to remain eligible for grants and scheme benefits.' },
    ],
  },

  DIGITAL_SIGNATURE: {
    intro: [
      'A Digital Signature Certificate (DSC) is the electronic equivalent of your physical signature, required to file most MCA, GST and Income Tax forms, sign documents digitally, and participate in e-tendering.',
      'SolveTax handles your DSC application with the certifying authority, including identity verification, and gets your token issued and ready to use.',
    ],
    eligibilityTitle: 'Who needs a DSC?',
    eligibility: [
      'Company directors and LLP partners signing MCA filings',
      'Authorized signatories filing GST returns or applications',
      'Anyone participating in government e-tenders or e-procurement',
      'Individuals filing income tax returns requiring digital signature (certain categories)',
    ],
    documents: [
      'PAN and Aadhaar of the applicant',
      'Passport-size photograph',
      'A working mobile number and email for video verification',
    ],
    benefits: [
      'A single Class 3 DSC covers MCA, GST and most e-tendering portal filings',
      'Legally equivalent to a physical signature for electronic filings',
    ],
    process: [
      { title: 'Identity verification', desc: 'PAN, Aadhaar and a video verification (as required by the certifying authority) are completed.' },
      { title: 'Application filed', desc: 'Submitted to a licensed Certifying Authority for a Class 3 DSC.' },
      { title: 'DSC issued on token', desc: 'Your digital signature is issued on a secure USB token, ready to use.' },
    ],
    timeline: 'DSC issuance typically takes 1–3 working days after successful verification.',
    faqs: [
      { q: 'What is a Class 3 DSC?', a: "Class 3 is the highest assurance level DSC, required for MCA filings, GST, e-tendering and most business/legal use cases in India." },
      { q: 'How long is a DSC valid?', a: 'Typically issued for 1, 2 or 3 years, after which it needs renewal.' },
      { q: 'Can I use one DSC for multiple purposes (MCA, GST, tenders)?', a: 'Generally yes, a Class 3 DSC can be used across MCA, GST and most e-tendering portals, subject to portal-specific registration of the DSC.' },
    ],
  },

  SHOP_ACT_REG: {
    intro: [
      'Shop and Establishment registration is a basic state-level requirement for any commercial establishment — shops, offices, warehouses — confirming compliance with working hours, wages and working condition regulations for employees.',
      'SolveTax identifies your state\'s specific requirements and completes your Shop & Establishment registration.',
    ],
    eligibilityTitle: 'Who needs this registration?',
    eligibility: [
      'Shops, commercial establishments and offices with or without employees',
      'Required within a specified period of commencing business, typically 30 days',
    ],
    documents: [
      'Business address proof',
      'PAN and ID proof of the proprietor/partners',
      'Details of employees, if any',
    ],
    benefits: [
      'Confirms compliance with working hours, wages and working condition norms',
      'Often required as supporting proof for opening a business bank account or applying for other licenses',
    ],
    process: [
      { title: 'Details collected', desc: 'Business address proof, PAN and details of employees (if any).' },
      { title: 'Application filed', desc: 'Filed with the state labor department or municipal body, depending on the state.' },
      { title: 'Certificate issued', desc: 'A registration certificate is issued, often needing periodic renewal.' },
    ],
    timeline: 'Typically issued within 7–15 working days, depending on the state.',
    faqs: [
      { q: 'Is this the same as a trade license?', a: 'They are separate registrations — Shop & Establishment registration relates to labor law compliance, while a trade license permits the specific business activity at that location. Many businesses need both.' },
      { q: 'Do I need this even with no employees?', a: 'In most states, yes — proprietors and small offices without employees still typically need to register.' },
      { q: 'Does this registration need renewal?', a: 'In many states yes, on a periodic basis — we track your renewal date so it doesn\'t lapse.' },
      { q: 'Is this required for a home-based or online-only business?', a: 'Requirements vary by state, but many jurisdictions still expect registration for any commercial activity, including home-based and online businesses — we confirm your specific requirement.' },
    ],
  },

  FIRE_LICENSE: {
    intro: [
      'A Fire Safety NOC (No Objection Certificate) from the local fire department confirms your premises meets fire safety norms — commonly required for restaurants, factories, large offices and any commercial building with public footfall.',
      'SolveTax coordinates the fire safety compliance review and NOC application with your local fire department.',
    ],
    eligibilityTitle: 'Who needs a Fire NOC?',
    eligibility: [
      'Restaurants, hotels and commercial establishments with public footfall',
      'Factories, warehouses and industrial premises',
      'Large office spaces above the area/height threshold set by local regulations',
    ],
    documents: [
      'Building layout/floor plan',
      'Fire safety equipment installation details',
      'Building completion/occupancy certificate',
    ],
    benefits: [
      'Mandatory sign-off for opening to the public in most commercial categories',
      'Confirms your premises meets fire safety standards, protecting occupants',
    ],
    process: [
      { title: 'Premises assessment', desc: 'Fire safety equipment and layout are reviewed against local fire code requirements.' },
      { title: 'Application filed', desc: 'Submitted to the local fire department with building and safety equipment details.' },
      { title: 'Inspection', desc: 'Fire department officials inspect the premises before granting the NOC.' },
      { title: 'NOC issued', desc: 'The Fire Safety NOC is issued once the premises passes inspection.' },
    ],
    faqs: [
      { q: 'What fire safety equipment is typically required?', a: 'Fire extinguishers, smoke detectors, emergency exits and, for larger premises, sprinkler systems — exact requirements depend on the size and use of your premises.' },
      { q: 'Does the Fire NOC need renewal?', a: 'Yes, it typically needs periodic renewal, along with re-inspection to confirm continued compliance.' },
      { q: 'How long does it take to get a Fire NOC?', a: 'Timelines vary by local fire department and premises complexity — typically a few weeks including the inspection step.' },
      { q: 'What happens if I operate without a Fire NOC?', a: 'Local authorities can order closure of the premises and impose penalties until the NOC is obtained, especially for public-facing establishments.' },
    ],
  },

  LEGAL_NAME_CHANGE: {
    intro: [
      "Changing your company or LLP's legal name requires board and shareholder approval, name availability check, and filing with the ROC — after which your PAN, GST, bank accounts and other registrations need to be updated to reflect the new name.",
      'SolveTax manages the entire name change process, including the follow-on updates across your other registrations.',
    ],
    eligibilityTitle: 'When do you need this filing?',
    eligibility: [
      'Rebranding your business under a new name',
      'Correcting an inappropriate or unavailable name after incorporation',
      'Business restructuring requiring a new legal identity',
    ],
    documents: [
      'Board resolution and special resolution approving the name change',
      'Proposed new name options',
      'Certificate of Incorporation (existing)',
    ],
    benefits: [
      'Legally secures your new brand name across all official records',
      'A properly documented change avoids confusion with contracts and registrations',
    ],
    process: [
      { title: 'Name approval', desc: 'A new name is reserved via the MCA RUN service, checking availability and compliance.' },
      { title: 'Board & shareholder approval', desc: 'Special resolution passed approving the name change and amendment to the MOA.' },
      { title: 'ROC filing', desc: 'Forms MGT-14 and INC-24 are filed with the Registrar.' },
      { title: 'New certificate & updates', desc: 'A fresh Certificate of Incorporation is issued; we help you update PAN, GST, bank and other registrations.' },
    ],
    timeline: 'The ROC approval typically takes 2–4 weeks; updating other registrations (PAN, GST, bank) can take additional time thereafter.',
    faqs: [
      { q: 'Does my company get a new CIN after a name change?', a: 'No, your CIN remains the same — only the name changes on your Certificate of Incorporation and all official records.' },
      { q: 'Do I need to update my PAN and GST after a name change?', a: "Yes, and this is often the most overlooked part of the process — we help make sure every registration is updated consistently." },
      { q: 'What existing contracts happen to under the old name?', a: 'Existing contracts generally remain valid; the new name simply needs to be reflected going forward, and counterparties should be informed of the change.' },
    ],
  },

  WATER_TESTING: {
    intro: [
      'Water testing certification confirms the quality and potability of water used in food production, bottling, or supplied to consumers — often a mandatory requirement for FSSAI licensing and certain manufacturing certifications.',
      'SolveTax coordinates sample collection and laboratory testing, and helps you obtain the certification report needed for your compliance requirement.',
    ],
    eligibilityTitle: 'Who needs water testing?',
    eligibility: [
      'Food and beverage manufacturers using water in production (often required for FSSAI)',
      'Packaged drinking water and bottling businesses',
      'Businesses required to demonstrate water quality compliance for a specific certification',
    ],
    documents: [
      'Details of the water source/point of use to be tested',
      'Business registration proof, for the certificate to be issued in your business name',
    ],
    benefits: [
      'Satisfies FSSAI and other regulatory water quality requirements',
      'Documented proof of water safety, useful for audits and buyer due diligence',
    ],
    process: [
      { title: 'Sample collected', desc: 'Water samples are collected from your specified source/point of use.' },
      { title: 'Laboratory testing', desc: 'Tested at an accredited lab against the relevant quality parameters (BIS/FSSAI standards).' },
      { title: 'Report issued', desc: 'A test report/certificate is issued reflecting the results against the applicable standard.' },
    ],
    timeline: 'Typically 5–10 working days from sample collection to report issuance.',
    faqs: [
      { q: 'How often does water testing need to be repeated?', a: 'This depends on your specific regulatory requirement — many licenses require periodic (often annual) re-testing.' },
      { q: 'Which standard is my water tested against?', a: 'Typically IS 10500 (drinking water) or FSSAI packaged water standards, depending on your use case — we confirm the right standard for your requirement.' },
      { q: 'What if my water sample fails the required parameters?', a: 'We help you understand which parameter failed and advise on filtration/treatment steps before a re-test.' },
      { q: 'Is this required for an FSSAI license application?', a: 'Yes, most FSSAI manufacturing license categories specifically require a valid water testing report as part of the application.' },
    ],
  },

  FOOD_TESTING: {
    intro: [
      'Food testing certification verifies that your food product meets safety and quality parameters required by FSSAI — checking for contaminants, nutritional accuracy, and compliance with food safety standards.',
      'SolveTax coordinates sample testing with an accredited laboratory and helps interpret and act on the results for your FSSAI compliance.',
    ],
    eligibilityTitle: 'Who needs food testing?',
    eligibility: [
      'Food manufacturers required to test products periodically under FSSAI norms',
      'Businesses launching a new food product needing safety/quality verification',
      'Exporters needing test certificates for international buyers',
    ],
    documents: [
      'Product sample(s) for testing',
      'FSSAI license number',
      'Product label/ingredient declaration',
    ],
    benefits: [
      'Confirms your product meets FSSAI safety and quality parameters',
      'Test certificates support export buyer due diligence requirements',
    ],
    process: [
      { title: 'Sample collected', desc: 'Product samples are collected and dispatched to an accredited food testing lab.' },
      { title: 'Testing conducted', desc: 'Samples are tested for the relevant safety and quality parameters based on product category.' },
      { title: 'Report issued', desc: 'A detailed test report is issued, which we help you interpret against FSSAI requirements.' },
    ],
    timeline: 'Typically 5–10 working days from sample collection to report issuance, depending on the parameters tested.',
    faqs: [
      { q: 'How often does FSSAI require food testing?', a: 'Frequency depends on your license category and product risk category — some products require more frequent testing than others.' },
      { q: 'What happens if my product fails testing?', a: 'We help you understand the specific parameter that failed and work with you on corrective steps before retesting.' },
      { q: 'Which lab conducts the testing?', a: 'We coordinate with FSSAI-accredited/NABL-recognized laboratories appropriate for your specific product category.' },
      { q: 'Do I need testing before launching a new product?', a: 'Yes, testing a new product before launch is strongly recommended to confirm safety and label compliance ahead of any regulatory check.' },
    ],
  },

  // ===========================================================================
  // CONSULTATION
  // ===========================================================================
  CA_CONSULTATION: {
    intro: [
      'CA Consultation is a paid, one-on-one 30-minute session with a verified, practicing Chartered Accountant — booked directly through this page, with no office visit required. It is meant for anyone who needs a specific, expert answer before making a tax or business decision, rather than a generic article or forum thread.',
      'On SolveTax, you fill a short form describing your question, get matched with a CA experienced in that exact area, and receive a call within the time window you choose. If you decide to proceed with an actual filing or registration afterward, the consultation carries directly into any of SolveTax\'s other services with no repeated explanation needed.',
    ],
    eligibilityTitle: 'When should you book a CA consultation?',
    eligibility: [
      'Deciding between business structures (proprietorship vs LLP vs Pvt Ltd) before registering',
      'Tax planning ahead of year-end, or understanding a large one-off transaction',
      'Getting a second opinion on a notice, assessment or existing filing',
      'Understanding GST, TDS or compliance obligations for a new business activity',
    ],
    documents: [
      'Any relevant notice, financial statement or document related to your query (optional but helpful)',
      'A short written note on what you want to cover, shared when booking',
    ],
    benefitsTitle: 'What you can do on SolveTax',
    benefits: [
      'Book a verified CA in under 2 minutes — just your name, number and question',
      'Choose the language you\'re most comfortable in: Telugu, Hindi or English',
      'Get matched to a CA experienced in your specific query, not a random agent',
      'Receive a written summary of the advice after the call, for your own record',
      'Move straight into filing, registration or notice-response services afterward — no need to re-explain your situation',
    ],
    process: [
      { title: 'Book your slot', desc: 'Fill the form on this page with your name, mobile number and a brief note on what you need help with.' },
      { title: 'CA assigned to your query', desc: 'We match you with a CA experienced in your specific area of question.' },
      { title: 'Consultation call', desc: 'A focused 30-minute call in your preferred language, covering your specific questions.' },
      { title: 'Summary & next steps', desc: 'You get a clear summary of the advice given and, if needed, next steps to act on it.' },
    ],
    faqs: [
      { q: 'How long is the consultation?', a: 'Sessions are typically 30 minutes, focused specifically on the questions you share when booking.' },
      { q: 'Can I get a consultation before deciding to file anything?', a: 'Yes — many people book a consultation purely to understand their options before committing to any specific filing or registration.' },
      { q: 'What if I need more time than one session?', a: 'You can book a follow-up session, or transition directly into one of our filing/registration services if you decide to proceed.' },
      { q: 'Is the consultation confidential?', a: 'Yes, anything you share is kept confidential and used only to advise you on your query.' },
      { q: 'What should I prepare before the call?', a: 'A brief note on your question and any relevant document (notice, statement, prior filing) helps the CA give sharper, more specific advice in the time available.' },
    ],
  },

  LEGAL_CONSULTATION: {
    intro: [
      'Legal Consultation is a paid, one-on-one 30-minute session with a verified legal expert — booked directly through this page, with no office visit required. It is meant for anyone who needs advice on a contract, notice or compliance question specific to their own documents, before committing to a formal legal engagement.',
      'On SolveTax, you fill a short form describing your question, get matched with a legal expert experienced in that exact area, and receive a call within the time window you choose. If you decide to move forward — drafting an agreement, responding to a notice, or a related filing — it connects directly into SolveTax\'s other services with no repeated explanation needed.',
    ],
    eligibilityTitle: 'When should you book a legal consultation?',
    eligibility: [
      'Reviewing or drafting a business contract or agreement',
      'Understanding legal obligations before a business decision',
      'A dispute or notice that needs legal interpretation',
      'General compliance or documentation questions for your business',
    ],
    documents: [
      'The specific contract, notice or document you want reviewed (optional but helpful)',
      'A short written note on your question, shared when booking',
    ],
    benefitsTitle: 'What you can do on SolveTax',
    benefits: [
      'Book a verified legal expert in under 2 minutes — just your name, number and question',
      'Choose the language you\'re most comfortable in: Telugu, Hindi or English',
      'Get matched to an expert experienced in your specific area of concern',
      'Receive a written summary of the guidance after the call, for your own record',
      'Move straight into drafting, notice-response or compliance services afterward — no need to re-explain your situation',
    ],
    process: [
      { title: 'Book your slot', desc: 'Fill the form with your name, mobile number and a brief note on your legal question.' },
      { title: 'Expert assigned', desc: 'We match you with a legal expert experienced in your specific area of concern.' },
      { title: 'Consultation call', desc: 'A focused 30-minute call in your preferred language addressing your specific questions.' },
      { title: 'Summary & next steps', desc: 'You receive a clear summary of the guidance given and recommended next steps.' },
    ],
    faqs: [
      { q: 'What kind of legal questions can I ask?', a: 'Business contracts, compliance obligations, notices, documentation and general legal questions relevant to running your business.' },
      { q: 'Is this a substitute for hiring a lawyer for litigation?', a: 'This consultation is meant for guidance and clarity — for active litigation or court representation, we can help direct you to the right next step.' },
      { q: 'How long is the session?', a: 'Sessions are typically 30 minutes, focused on the specific questions you share at the time of booking.' },
      { q: 'Can I share a contract or document for the expert to review before the call?', a: 'Yes, sharing it in advance when you book lets the expert review it beforehand and use the call time more effectively.' },
    ],
  },
};

export default SERVICE_CONTENT;
